import type * as MpSdk from '../bundle/sdk';
import type { ProductItem } from './products';
import { MATTERTAG_PRODUCT_CONFIG } from './mattertag-item-map';
import { fetchLatestProductsByFilters, fetchProductsByItemCodes } from './product-api';
import { createDigitalMirrorClient, getDigitalMirrorConfigResult } from './digital-mirror';
import type { DigitalMirrorClient } from './digital-mirror';
import { trackClick, trackEngagement, trackExit } from './analytics';
//npx webpack --config webpack.config.js


// 自動（latest）で表示するデフォルト件数
const DEFAULT_AUTO_LIMIT = 10;

// --- Matterport Tag Icon Override ---
// Tag disc: white, icon: purple dot (matches the provided design).
const CUSTOM_TAG_ICON_ENABLED = true;
const CUSTOM_TAG_ICON_TEXTURE_ID = 'daitona.tagIcon.dotPurple.v1';
const CUSTOM_TAG_ICON_SRC_PATH = 'bundle/images/tag-dot.png';
const CUSTOM_TAG_DISC_COLOR: MpSdk.Color = { r: 1, g: 1, b: 1 };
// true にすると、タグクリック時の Matterport 標準タイトル/ビルボード表示を許可します。
const ENABLE_DEFAULT_TAG_TITLE_BILLBOARD = false;
let defaultTagTitleBillboardVisible = ENABLE_DEFAULT_TAG_TITLE_BILLBOARD;
let currentSdkForTagTitleControl: MpSdk.CommonMpSdk | null = null;
let tagTitleActionGuardSubscription: { cancel: () => void } | null = null;

const isTagTitleBillboardVisible = () => defaultTagTitleBillboardVisible;

const clearTagTitleActionGuardSubscription = () => {
  if (!tagTitleActionGuardSubscription) return;
  try {
    tagTitleActionGuardSubscription.cancel();
  } catch {}
  tagTitleActionGuardSubscription = null;
};

const applyTagTitleAction = (sdk: MpSdk.CommonMpSdk, tagSid: string, visible: boolean) => {
  const sid = String(tagSid ?? '').trim();
  if (!sid) return;
  const sdkAny = sdk as any;

  if (visible) {
    if (sdkAny.Tag?.allowAction) {
      sdkAny.Tag.allowAction(sid, { opening: true, navigating: true }).catch(() => {});
    }
    if (sdkAny.Mattertag?.preventAction) {
      sdkAny.Mattertag.preventAction(sid, { opening: false, navigating: false }).catch(() => {});
    }
    return;
  }

  if (sdkAny.Tag?.allowAction) {
    sdkAny.Tag.allowAction(sid, {}).catch(() => {});
  }
  if (sdkAny.Tag?.close) {
    sdkAny.Tag.close(sid).catch(() => {});
  }
  if (sdkAny.Mattertag?.preventAction) {
    sdkAny.Mattertag.preventAction(sid, { opening: true, navigating: true }).catch(() => {});
  }
};

const applyTagTitleBillboardVisibility = (sdk: MpSdk.CommonMpSdk, visible: boolean) => {
  clearTagTitleActionGuardSubscription();

  const targetSids = Object.keys(MATTERTAG_PRODUCT_CONFIG);
  targetSids.forEach((sid) => applyTagTitleAction(sdk, sid, visible));

  if (visible) return;

  // Mobile runtimes can re-enable actions as Tag.data updates; keep applying defensively.
  const sdkAny = sdk as any;
  if (sdkAny.Tag?.data?.subscribe && sdkAny.Tag?.allowAction) {
    const targetLower = new Set(targetSids.map((sid) => sid.toLowerCase()));
    const applyIfTarget = (id: string) => {
      const normalized = String(id ?? '').trim();
      if (!normalized) return;
      if (!targetLower.has(normalized.toLowerCase())) return;
      applyTagTitleAction(sdk, normalized, false);
    };

    const sub = sdkAny.Tag.data.subscribe({
      onAdded(_index: string, item: any) {
        applyIfTarget(String(item?.id ?? ''));
      },
      onUpdated(_index: string, item: any) {
        applyIfTarget(String(item?.id ?? ''));
      },
    });

    if (sub && typeof sub.cancel === 'function') {
      tagTitleActionGuardSubscription = sub;
    }
  }
};

const setTagTitleBillboardVisible = (visible: boolean) => {
  defaultTagTitleBillboardVisible = Boolean(visible);
  if (currentSdkForTagTitleControl) {
    applyTagTitleBillboardVisibility(currentSdkForTagTitleControl, defaultTagTitleBillboardVisible);
  }
  console.log(
    '[TagTitle] default title billboard:',
    defaultTagTitleBillboardVisible ? 'visible' : 'hidden'
  );
};

// Runtime toggles (dev console):
// - window.setTagTitleVisible(true | false)
// - window.toggleTagTitleVisible()
// - window.getTagTitleVisible()
(window as any).setTagTitleVisible = (visible: boolean) => {
  setTagTitleBillboardVisible(Boolean(visible));
  return isTagTitleBillboardVisible();
};
(window as any).toggleTagTitleVisible = () => {
  setTagTitleBillboardVisible(!isTagTitleBillboardVisible());
  return isTagTitleBillboardVisible();
};
(window as any).getTagTitleVisible = () => isTagTitleBillboardVisible();

const setupCustomTagIcons = async (sdk: MpSdk.CommonMpSdk) => {
  if (!CUSTOM_TAG_ICON_ENABLED) return;

  const iconSrc = new URL(CUSTOM_TAG_ICON_SRC_PATH, window.location.href).toString();
  const hasTagApi = Boolean(sdk.Tag?.editIcon && sdk.Tag?.editColor && sdk.Tag?.data?.subscribe);
  const hasMattertagApi = Boolean(sdk.Mattertag?.editIcon && sdk.Mattertag?.editColor && sdk.Mattertag?.data?.subscribe);

  if (!hasTagApi && !hasMattertagApi) {
    console.warn('[TagIcon] Tag icon override is not supported by this SDK/runtime.');
    return;
  }

  // Register texture for Tag.editIcon (preferred)
  let tagTextureRegistered = false;
  if (sdk.Asset?.registerTexture) {
    try {
      await sdk.Asset.registerTexture(CUSTOM_TAG_ICON_TEXTURE_ID, iconSrc);
      tagTextureRegistered = true;
    } catch (e) {
      console.warn('[TagIcon] Failed to register texture (Asset.registerTexture)', e);
    }
  }

  // Register icon for legacy Mattertag.editIcon fallback
  let mattertagIconRegistered = false;
  if (sdk.Mattertag?.registerIcon) {
    try {
      await sdk.Mattertag.registerIcon(CUSTOM_TAG_ICON_TEXTURE_ID, iconSrc);
      mattertagIconRegistered = true;
    } catch (e) {
      console.warn('[TagIcon] Failed to register icon (Mattertag.registerIcon)', e);
    }
  }

  const originalTagColor = new Map<string, MpSdk.Color>();
  const originalMattertagColor = new Map<string, MpSdk.Color>();
  const applied = new Set<string>();
  const failed = new Set<string>();
  const applyToTag = async (tagId: string) => {
    if (!tagId) return;
    if (applied.has(tagId) || failed.has(tagId)) return;

    // Preferred API (Tag.*)
    if (hasTagApi && tagTextureRegistered) {
      try {
        await sdk.Tag.editOpacity(tagId, 1);
        await sdk.Tag.editColor(tagId, CUSTOM_TAG_DISC_COLOR);
        await sdk.Tag.editIcon(tagId, CUSTOM_TAG_ICON_TEXTURE_ID);
        applied.add(tagId);
        return;
      } catch (e) {
        // Fall through to legacy Mattertag API
        console.warn('[TagIcon] Failed to apply via Tag API. Falling back...', tagId, e);
      }
    }

    // Legacy API fallback (Mattertag.*)
    if (hasMattertagApi && mattertagIconRegistered) {
      try {
        await sdk.Mattertag.editOpacity(tagId, 1);
        await sdk.Mattertag.editColor(tagId, CUSTOM_TAG_DISC_COLOR);
        await sdk.Mattertag.editIcon(tagId, CUSTOM_TAG_ICON_TEXTURE_ID);
        applied.add(tagId);
        return;
      } catch (e) {
        console.warn('[TagIcon] Failed to apply via Mattertag API', tagId, e);
      }
    }

    // If we couldn't apply the custom icon (e.g., texture registration failed),
    // restore the original look so tags don't become effectively invisible.
    try {
      if (hasTagApi) {
        await sdk.Tag.editOpacity(tagId, 1);
        const orig = originalTagColor.get(tagId);
        if (orig) await sdk.Tag.editColor(tagId, orig);
        await sdk.Tag.resetIcon(tagId);
      }
    } catch {}
    try {
      if (hasMattertagApi) {
        await sdk.Mattertag.editOpacity(tagId, 1);
        const orig = originalMattertagColor.get(tagId);
        if (orig) await sdk.Mattertag.editColor(tagId, orig);
        await sdk.Mattertag.resetIcon(tagId);
      }
    } catch {}

    failed.add(tagId);
  };

  if (hasTagApi) {
    sdk.Tag.data.subscribe({
      onAdded(_index, item) {
        if (!originalTagColor.has(item.id) && item.color) {
          originalTagColor.set(item.id, { r: item.color.r, g: item.color.g, b: item.color.b });
        }
        void applyToTag(item.id);
      },
      onUpdated(_index, item) {
        if (!originalTagColor.has(item.id) && item.color) {
          originalTagColor.set(item.id, { r: item.color.r, g: item.color.g, b: item.color.b });
        }
        void applyToTag(item.id);
      },
    });
  }

  if (hasMattertagApi) {
    sdk.Mattertag.data.subscribe({
      onAdded(_index, item) {
        if (!originalMattertagColor.has(item.sid) && item.color) {
          originalMattertagColor.set(item.sid, { r: item.color.r, g: item.color.g, b: item.color.b });
        }
        void applyToTag(item.sid);
      },
      onUpdated(_index, item) {
        if (!originalMattertagColor.has(item.sid) && item.color) {
          originalMattertagColor.set(item.sid, { r: item.color.r, g: item.color.g, b: item.color.b });
        }
        void applyToTag(item.sid);
      },
    });
  }
};

const showcase = document.getElementById('showcase') as HTMLIFrameElement;
const key = 'YOUR_KEY_HERE';
// mappingファイルに書いた Mattertag SID を使う（デバッグ用途）
const modalTagSid = Object.keys(MATTERTAG_PRODUCT_CONFIG)[0] ?? '8D9qWf90vTQ';
const floatingList = document.getElementById('floating-list') as HTMLDivElement | null;

const modalBackdrop = document.getElementById('modal-backdrop');
const modalClose = document.getElementById('modal-close');
const modalBody = document.getElementById('modal-body');
type DigitalMirrorOpenSource = 'tryon' | 'tap';

const clearModalLayoutClasses = () => {
  if (!modalBackdrop) return;
  modalBackdrop.classList.remove('mirror-tap-layout', 'item-detail-layout');
};

const applyDefaultModalLayout = () => {
  clearModalLayoutClasses();
  if (modalClose instanceof HTMLElement) modalClose.style.display = '';
};

const applyTapMirrorLayout = () => {
  clearModalLayoutClasses();
  if (modalBackdrop) modalBackdrop.classList.add('mirror-tap-layout');
  if (modalClose instanceof HTMLElement) modalClose.style.display = 'none';
};

const applyItemDetailLayout = () => {
  clearModalLayoutClasses();
  if (modalBackdrop) modalBackdrop.classList.add('item-detail-layout');
  if (modalClose instanceof HTMLElement) modalClose.style.display = 'none';
};

const hideModal = () => {
  if (!modalBackdrop) return;
  modalBackdrop.classList.remove('visible');
  applyDefaultModalLayout();
};

// --- Digital Mirror (iframe + postMessage) ---
let digitalMirrorClient: DigitalMirrorClient | null = null;

const destroyDigitalMirrorClient = () => {
  if (!digitalMirrorClient) return;
  try {
    digitalMirrorClient.destroy();
  } catch (e) {
    console.warn('[DigitalMirror] destroy failed', e);
  }
  digitalMirrorClient = null;
};

// --- CG (Tap Sign Texture) ---
// A clickable texture billboard placed in the 3D scene.
// NOTE: This path is resolved relative to `bundle/showcase.html` (i.e. inside the iframe).
const TAP_SIGN_ENABLED = true;
const TAP_SIGN_TEXTURE_PRIMARY_PATH_IN_SHOWCASE = '../images/tap.gif';
const TAP_SIGN_TEXTURE_FALLBACK_PATH_IN_SHOWCASE = './images/tap.png';
const TAP_SIGN_TEXTURE_PATH_IN_SHOWCASE = TAP_SIGN_TEXTURE_PRIMARY_PATH_IN_SHOWCASE;
// Diameter in world meters
const TAP_SIGN_DIAMETER_METERS = 0.3;
// If true, place near the entrance by raycasting the floor from the viewport.
// If false, use TAP_SIGN_POSITION as-is.
const TAP_SIGN_PLACE_AT_ENTRANCE = false;
// Fallback position if we fail to compute the "entrance" floor point
const TAP_SIGN_POSITION: MpSdk.Vector3 = {
    "x": 7.0,
    "y": 1.2777372054592473,
    "z": -4.5
};
const TAP_SIGN_ORIENTATION: MpSdk.Orientation = { yaw: 0, pitch: 0, roll: 0 };
// Rotate to face the camera (yaw-only) each frame
const TAP_SIGN_BILLBOARD = true;

// --- CG (Animated Model: GLB/GLTF/FBX) ---
// NOTE:
// - This path is resolved relative to `bundle/showcase.html` (i.e. inside the iframe).
// - Put your model (.glb/.gltf/.fbx) under `bundle/models/` and update this value.
// Turn this off to disable the CG model entirely (no registration, no load).
const CG_GLB_ENABLED = true;
const CG_GLB_PATH_IN_SHOWCASE = './models/Petal_Animation.glb';
// World-space position (meters)
const CG_GLB_POSITION: MpSdk.Vector3 = {
  x: 6.52697053616155,
  y: -0.01743441681800828,
  z: 0.3,
};
// Second model instance
const CG_GLB_SECOND_ENABLED = true;
const CG_GLB_SECOND_PATH_IN_SHOWCASE = './models/CherryBlossomTree.glb';
const CG_GLB_SECOND_POSITION: MpSdk.Vector3 = {
  x: 6.52697053616155,
  y: -0,
  z: 0.3,
};
// If true, place near the entrance by raycasting the floor from the viewport.
// If false, use CG_GLB_POSITION as-is.
const CG_GLB_PLACE_AT_ENTRANCE = false;
// Orientation in degrees (yaw->pitch->roll). Use yaw (Y) only if you just want to rotate horizontally.
const CG_GLB_ORIENTATION: MpSdk.Orientation = { yaw: 0, pitch: 0, roll: 0 };
// Scale (1 = original)
const CG_GLB_SCALE: MpSdk.Vector3 = { x: 0.7, y: 1.0, z: 0.7 };
const CG_GLB_SECOND_SCALE: MpSdk.Vector3 = { x: 0.9, y: 1.4, z: 0.9 };
// Optional: auto-fit the model so its height becomes ~N meters (helps when GLB units are not meters).
const CG_GLB_AUTO_FIT_HEIGHT = true;
// NOTE: For display verification, make it intentionally large.
const CG_GLB_FIT_HEIGHT_METERS = 3.0;
// Many Matterport scenes are effectively "unlit" for custom GLTF PBR materials.
// We prefer fixing the model materials (emissive/PBR overrides) instead of adding global lights.
const CG_GLB_ADD_LIGHTS = false;
const CG_GLB_AMBIENT_LIGHT_INTENSITY = 1.6;
const CG_GLB_DIRECTIONAL_LIGHT_INTENSITY = 2.2;
// If you want the model to render "as-is" from its textures (ignore lighting/shadows),
// set this true to force unlit materials (MeshBasicMaterial).
// This is the most reliable way to avoid "black" PBR when the scene has insufficient lighting/IBL.
const CG_GLB_FORCE_UNLIT_MATERIALS = true;
// Initial visibility
const CG_GLB_INITIAL_VISIBLE = true;
// Simple UI toggle button (also exposes window.setCgVisible / window.toggleCgVisible)
const CG_GLB_ENABLE_TOGGLE_BUTTON = false;
const CG_GLB_TOGGLE_BUTTON_TEXT_VISIBLE = '3Dモデル ON';
const CG_GLB_TOGGLE_BUTTON_TEXT_HIDDEN = '3Dモデル OFF';
// If the model has no embedded animation clips, we can optionally apply a slow debug spin
// so you can confirm it's rendered. Disable this for normal usage.
const CG_GLB_FALLBACK_SPIN = false;
const CG_GLB_FALLBACK_SPIN_DEG_PER_SEC = 30;

const CG_TOGGLE_BUTTON_ID = 'cg-toggle';
let cgToggleBtn: HTMLButtonElement | null = null;

// If the previous build injected the toggle button, make sure it doesn't linger
// when the feature is disabled.
if (!CG_GLB_ENABLE_TOGGLE_BUTTON) {
  const existing = document.getElementById(CG_TOGGLE_BUTTON_ID);
  if (existing) existing.remove();
}

function getCgComponent(): any | null {
  return (window as any).__CG_GLB_COMPONENT__ ?? null;
}

function getTapSignComponent(): any | null {
  return (window as any).__TAP_SIGN_COMPONENT__ ?? null;
}

function toFiniteNumber(value: unknown, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function toVector3Like(raw: any, fallback: MpSdk.Vector3 = { x: 0, y: 0, z: 0 }): MpSdk.Vector3 {
  return {
    x: toFiniteNumber(raw?.x, fallback.x),
    y: toFiniteNumber(raw?.y, fallback.y),
    z: toFiniteNumber(raw?.z, fallback.z),
  };
}

function getCgPosition(): MpSdk.Vector3 | null {
  const comp = getCgComponent();
  if (!comp?.inputs) return null;
  return toVector3Like((comp.inputs as any).position, CG_GLB_POSITION);
}

function setCgPosition(x: number, y: number, z: number): MpSdk.Vector3 | null {
  const comp = getCgComponent();
  if (!comp) {
    console.warn('[CG] setCgPosition called but component is not ready yet');
    return null;
  }
  if (!comp.inputs) comp.inputs = {};

  const pos = {
    x: toFiniteNumber(x),
    y: toFiniteNumber(y),
    z: toFiniteNumber(z),
  };
  (comp.inputs as any).position = pos;
  console.log('[CG] position set:', pos);
  return pos;
}

function nudgeCgPosition(dx = 0, dy = 0, dz = 0): MpSdk.Vector3 | null {
  const base = getCgPosition();
  if (!base) {
    console.warn('[CG] nudgeCgPosition called but component is not ready yet');
    return null;
  }
  return setCgPosition(base.x + toFiniteNumber(dx), base.y + toFiniteNumber(dy), base.z + toFiniteNumber(dz));
}

function getTapSignPosition(): MpSdk.Vector3 | null {
  const comp = getTapSignComponent();
  if (!comp?.inputs) return null;
  return toVector3Like((comp.inputs as any).position, TAP_SIGN_POSITION);
}

function setTapSignPosition(x: number, y: number, z: number): MpSdk.Vector3 | null {
  const comp = getTapSignComponent();
  if (!comp) {
    console.warn('[TapSign] setTapSignPosition called but component is not ready yet');
    return null;
  }
  if (!comp.inputs) comp.inputs = {};

  const pos = {
    x: toFiniteNumber(x),
    y: toFiniteNumber(y),
    z: toFiniteNumber(z),
  };
  (comp.inputs as any).position = pos;
  console.log('[TapSign] position set:', pos);
  return pos;
}

function nudgeTapSignPosition(dx = 0, dy = 0, dz = 0): MpSdk.Vector3 | null {
  const base = getTapSignPosition();
  if (!base) {
    console.warn('[TapSign] nudgeTapSignPosition called but component is not ready yet');
    return null;
  }
  return setTapSignPosition(base.x + toFiniteNumber(dx), base.y + toFiniteNumber(dy), base.z + toFiniteNumber(dz));
}

function getCgVisible(): boolean {
  const comp = getCgComponent();
  const v = (comp?.inputs as any)?.visible;
  return v === undefined ? true : Boolean(v);
}

function ensureCgToggleButton() {
  if (!CG_GLB_ENABLE_TOGGLE_BUTTON) return null;

  const existing = document.getElementById(CG_TOGGLE_BUTTON_ID) as HTMLButtonElement | null;
  if (existing) {
    cgToggleBtn = existing;
    return existing;
  }

  const btn = document.createElement('button');
  btn.id = CG_TOGGLE_BUTTON_ID;
  btn.type = 'button';
  btn.setAttribute('aria-label', '3Dモデル 表示切替');
  btn.style.position = 'fixed';
  btn.style.right = '16px';
  btn.style.bottom = '180px';
  btn.style.zIndex = '1001';
  btn.style.padding = '10px 12px';
  btn.style.borderRadius = '999px';
  btn.style.border = '1px solid #8b5cf6';
  btn.style.background = 'rgba(255, 255, 255, 0.85)';
  btn.style.color = '#8b5cf6';
  btn.style.fontSize = '12px';
  btn.style.fontWeight = '700';
  btn.style.cursor = 'pointer';
  btn.style.display = 'none';
  btn.style.userSelect = 'none';
  btn.style.webkitTapHighlightColor = 'transparent';

  btn.dataset.bound = '1';
  btn.addEventListener('click', () => {
    toggleCgVisible();
  });

  document.body.appendChild(btn);
  cgToggleBtn = btn;
  return btn;
}

function updateCgToggleButton() {
  if (!CG_GLB_ENABLE_TOGGLE_BUTTON) return;
  const btn = ensureCgToggleButton();
  if (!btn) return;

  const visible = getCgVisible();
  btn.textContent = visible ? CG_GLB_TOGGLE_BUTTON_TEXT_VISIBLE : CG_GLB_TOGGLE_BUTTON_TEXT_HIDDEN;
  btn.style.opacity = visible ? '1' : '0.65';
}

function setCgVisible(visible: boolean) {
  const comp = getCgComponent();
  if (!comp) {
    console.warn('[CG] setCgVisible called but component is not ready yet');
    return;
  }
  if (!comp.inputs) comp.inputs = {};
  (comp.inputs as any).visible = Boolean(visible);
  updateCgToggleButton();
}

function toggleCgVisible() {
  setCgVisible(!getCgVisible());
}

// Expose tiny API for debugging / external control
(window as any).setCgVisible = setCgVisible;
(window as any).toggleCgVisible = toggleCgVisible;
(window as any).getCgVisible = getCgVisible;
(window as any).getCgPosition = getCgPosition;
(window as any).setCgPosition = setCgPosition;
(window as any).nudgeCgPosition = nudgeCgPosition;
(window as any).getTapSignPosition = getTapSignPosition;
(window as any).setTapSignPosition = setTapSignPosition;
(window as any).nudgeTapSignPosition = nudgeTapSignPosition;

let sliderState: {
  currentPage: number;
  totalPages: number;
  gridEl: HTMLElement | null;
  dotsEl: HTMLElement | null;
  prevBtn: HTMLButtonElement | null;
  nextBtn: HTMLButtonElement | null;
} = {
  currentPage: 0,
  totalPages: 0,
  gridEl: null,
  dotsEl: null,
  prevBtn: null,
  nextBtn: null,
};
const PAGE_SIZE = 3;
const TRYON_LIMIT = 5;
const TRYON_PAGE_SIZE = TRYON_LIMIT;
// ITEM DETAIL の表示先:
// - true: 新規タブで開く（現在運用）
// - false: モーダル内 iframe で開く（将来復活用）
const ITEM_DETAIL_OPEN_IN_NEW_TAB = false;
const tryOnList = new Set<string>();
const tryOnSelected = new Set<string>();
// --- Analytics state (Tag tracking) ---
let activeTagSid: string | null = null;
type ModalType = 'none' | 'tag' | 'tryon' | 'ec_iframe' | 'plusmirror';
let currentModalType: ModalType = 'none';
let modalOpenedAt = 0;
let modalContext: {
  tagSid?: string;
  productUrl?: string;
  productSkus?: string[];
} = {};

let latestFloorContext: {
  floorId?: string;
  floorSequence?: number;
  floorName?: string;
} = {};

let activeSweepContext: {
  spotId: string;
  spotLabel: string;
  floorId?: string;
  floorSequence?: number;
  enteredAt: number;
} | null = null;

let floorTrackingInitialized = false;
let lastTrackedFloorId: string | null = null;
let sdkAnalyticsSubscriptions: MpSdk.ISubscription[] = [];

const sweepLabelCache = new Map<string, string>();
let lastFullscreenState =
  Boolean(document.fullscreenElement) || Boolean((document as any).webkitFullscreenElement);

const getNowMs = () => Date.now();

const getDurationSec = (startedAtMs: number): number => {
  if (!startedAtMs) return 0;
  return Math.max(0, Math.floor((getNowMs() - startedAtMs) / 1000));
};

const setModalState = (
  type: ModalType,
  context: {
    tagSid?: string;
    productUrl?: string;
    productSkus?: string[];
  } = {}
) => {
  currentModalType = type;
  modalOpenedAt = getNowMs();
  modalContext = context;
};

const clearModalState = () => {
  currentModalType = 'none';
  modalOpenedAt = 0;
  modalContext = {};
};

const trackEcIframeClose = (closeMethod: 'back' | 'x') => {
  const url = String(modalContext.productUrl ?? '');
  trackClick('daytonapark_closebutton', closeMethod, {
    product_url: url,
    close_method: closeMethod,
  });
  trackEngagement('daytonapark', getDurationSec(modalOpenedAt), url || '(empty_url)', {
    product_url: url,
    close_method: closeMethod,
  });
};

const trackPlusMirrorClose = (closeMethod: 'back' | 'x' | 'app') => {
  const skuList = Array.isArray(modalContext.productSkus) ? modalContext.productSkus : [];
  trackClick('plusmirror_closebutton', closeMethod, {
    close_method: closeMethod,
  });
  trackEngagement('plusmirror', getDurationSec(modalOpenedAt), 'plusmirror', {
    close_method: closeMethod,
    selected_count: skuList.length,
    sku_list: skuList.join(','),
  });
};


/**
 * 取得済みの商品をキャッシュして、試着リストなどで参照できるようにします。
 * - key: ProductItem.id
 */
const productCache = new Map<string, ProductItem>();

/**
 * 現在モーダル（ITEM LIST）に表示する商品リスト。
 * 固定版では 1件のみを想定しています。
 */
let sliderProducts: ProductItem[] = [];
let tryOnState: {
  currentPage: number;
  totalPages: number;
  listEl: HTMLElement | null;
  dotsEl: HTMLElement | null;
  prevBtn: HTMLButtonElement | null;
  nextBtn: HTMLButtonElement | null;
  countEl: HTMLElement | null;
  countMainEl: HTMLElement | null;
  countSubEl: HTMLElement | null;
  ctaEl: HTMLButtonElement | null;
  alertEl: HTMLElement | null;
} = {
  currentPage: 0,
  totalPages: 0,
  listEl: null,
  dotsEl: null,
  prevBtn: null,
  nextBtn: null,
  countEl: null,
  countMainEl: null,
  countSubEl: null,
  ctaEl: null,
  alertEl: null,
};

const setFloatingListVisible = (show: boolean) => {
  if (!floatingList) return;
  floatingList.style.display = show ? 'block' : 'none';
};

const getCurrentFullscreenState = () =>
  Boolean(document.fullscreenElement) || Boolean((document as any).webkitFullscreenElement);

const getSweepId = (raw: any): string => {
  const id = String(raw?.id ?? raw?.sid ?? '').trim();
  return id;
};

const getSweepFloorId = (raw: any): string | undefined => {
  const id = String(raw?.floorInfo?.id ?? '').trim();
  return id.length > 0 ? id : undefined;
};

const getSweepFloorSequence = (raw: any): number | undefined => {
  const seq = Number(raw?.floorInfo?.sequence);
  return Number.isFinite(seq) ? seq : undefined;
};

const getSweepLabelCached = async (sdk: MpSdk.CommonMpSdk, sweepId: string): Promise<string> => {
  const id = String(sweepId ?? '').trim();
  if (!id) return '';
  const cached = sweepLabelCache.get(id);
  if (cached !== undefined) return cached;

  try {
    const labelRaw = await sdk.Sweep.Conversion.getLabelFromId(id);
    const label = String(labelRaw ?? '').trim() || id;
    sweepLabelCache.set(id, label);
    return label;
  } catch (e) {
    console.warn('[Analytics] Failed to resolve sweep label', id, e);
    sweepLabelCache.set(id, id);
    return id;
  }
};

const flushActiveSweepEngagement = (reason: 'switch' | 'pagehide') => {
  if (!activeSweepContext) return;

  const durationSec = getDurationSec(activeSweepContext.enteredAt);
  if (durationSec >= 1) {
    trackEngagement('floor', durationSec, activeSweepContext.spotId, {
      spot_id: activeSweepContext.spotId,
      spot_label: activeSweepContext.spotLabel,
      floor_id: activeSweepContext.floorId,
      floor_sequence: activeSweepContext.floorSequence,
    });
  }

  if (reason === 'pagehide') {
    trackExit(
      {
        spot_id: activeSweepContext.spotId,
        spot_label: activeSweepContext.spotLabel,
        floor_id: activeSweepContext.floorId,
        floor_sequence: activeSweepContext.floorSequence,
      },
      'floor',
      activeSweepContext.spotId
    );
  }
};

const findShareTarget = (target: EventTarget | null): Element | null => {
  let el: Element | null = target instanceof Element ? target : null;
  const pattern = /(share|共有|シェア)/i;

  while (el) {
    const ariaLabel = String(el.getAttribute('aria-label') ?? '');
    const testId = String(el.getAttribute('data-testid') ?? '');
    if (pattern.test(ariaLabel) || /share/i.test(testId)) return el;
    el = el.parentElement;
  }

  return null;
};

const setupShareClickTracking = () => {
  try {
    const showcaseDoc = showcase.contentWindow?.document;
    if (!showcaseDoc) return;
    showcaseDoc.addEventListener(
      'click',
      (evt: Event) => {
        if (!findShareTarget(evt.target)) return;
        trackClick('sns_share', 'click');
      },
      true
    );
  } catch (e) {
    // If this fails due to cross-origin or runtime constraints, ignore safely.
    console.warn('[Analytics] Failed to attach SNS share tracking', e);
  }
};

let modelViewContainerObserver: MutationObserver | null = null;

const setupModelViewContainerHidden = () => {
  try {
    const showcaseDoc = showcase.contentWindow?.document;
    if (!showcaseDoc) return;

    const hide = () => {
      const targets = showcaseDoc.querySelectorAll<HTMLElement>('.model-view-container');
      targets.forEach((el) => {
        el.style.setProperty('display', 'none', 'important');
        el.setAttribute('aria-hidden', 'true');
      });
    };

    hide();

    if (modelViewContainerObserver) {
      modelViewContainerObserver.disconnect();
      modelViewContainerObserver = null;
    }

    if (typeof MutationObserver !== 'undefined') {
      const root = showcaseDoc.documentElement ?? showcaseDoc.body;
      if (root) {
        modelViewContainerObserver = new MutationObserver(() => hide());
        modelViewContainerObserver.observe(root, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'style'],
        });
      }
    }
  } catch (e) {
    console.warn('[UI] Failed to hide model-view-container', e);
  }
};

document.addEventListener('fullscreenchange', () => {
  const next = getCurrentFullscreenState();
  if (next === lastFullscreenState) return;
  lastFullscreenState = next;
  trackClick('full_screen', next ? 'enter' : 'exit');
});

document.addEventListener('webkitfullscreenchange', () => {
  const next = getCurrentFullscreenState();
  if (next === lastFullscreenState) return;
  lastFullscreenState = next;
  trackClick('full_screen', next ? 'enter' : 'exit');
});

window.addEventListener('pagehide', () => {
  flushActiveSweepEngagement('pagehide');
  activeSweepContext = null;
});

const showProductModal = () => {
  if (!modalBackdrop || !modalBody) return;
  applyDefaultModalLayout();
  modalBody.innerHTML = '';
  setModalState('tag', { tagSid: activeTagSid ?? undefined });
  buildProductSlider(modalBody);
  modalBackdrop.classList.add('visible');
};

const getTryOnListProducts = () => {
  return Array.from(tryOnList)
    .map((id) => productCache.get(id))
    .filter((v): v is ProductItem => Boolean(v));
};

const getSelectedProducts = () => {
  return Array.from(tryOnList)
    .filter((id) => tryOnSelected.has(id))
    .map((id) => productCache.get(id))
    .filter((v): v is ProductItem => Boolean(v));
};

const setTryOnAlert = (message: string) => {
  const alertEl = tryOnState.alertEl;
  if (!alertEl) return;
  alertEl.textContent = message;
  alertEl.style.display = message ? 'block' : 'none';
};

const clearTryOnAlert = () => {
  setTryOnAlert('');
};

const getSelectedSkus = (): string[] | null => {
  const selected = getSelectedProducts();
  if (selected.length === 0) return [];
  if (selected.length > TRYON_LIMIT) {
    window.alert('試着点数は5点までです。');
    return null;
  }

  const missingSku = selected.some((item) => String(item.sku ?? '').trim().length === 0);
  if (missingSku) {
    window.alert('SKUが取得できない商品が含まれています。商品情報を再取得してください。');
    return null;
  }

  return Array.from(
    new Set(
      selected
        .map((item) => String(item.sku ?? '').trim())
        .filter(Boolean)
    )
  );
};

const renderTryOnPage = (page: number) => {
  const { listEl, dotsEl, prevBtn, nextBtn, countMainEl, countSubEl, ctaEl } = tryOnState;
  if (!listEl || !dotsEl || !prevBtn || !nextBtn || !countMainEl || !countSubEl || !ctaEl) return;

  // Keep selection consistent when items are removed from the list.
  Array.from(tryOnSelected).forEach((id) => {
    if (!tryOnList.has(id)) tryOnSelected.delete(id);
  });

  const listed = getTryOnListProducts();
  const totalPages = Math.max(1, Math.ceil(listed.length / TRYON_PAGE_SIZE));
  tryOnState.totalPages = totalPages;
  tryOnState.currentPage = Math.min(page, totalPages - 1);

  const start = tryOnState.currentPage * TRYON_PAGE_SIZE;
  const end = start + TRYON_PAGE_SIZE;
  const slice = listed.slice(start, end);

  listEl.innerHTML = '';
  if (slice.length === 0) {
    const empty = document.createElement('div');
    empty.textContent = '試着リストは空です';
    empty.style.color = '#888';
    empty.style.fontSize = '12px';
    listEl.appendChild(empty);
  } else {
    slice.forEach((item) => {
      const row = document.createElement('div');
      row.className = 'tryon-item';

      const isChecked = tryOnSelected.has(item.id);
      const check = document.createElement('button');
      check.className = `tryon-check${isChecked ? ' checked' : ''}`;
      check.type = 'button';
      check.textContent = isChecked ? '✓' : '';
      check.setAttribute('aria-pressed', String(isChecked));
      check.addEventListener('click', () => {
        if (tryOnSelected.has(item.id)) {
          tryOnSelected.delete(item.id);
          clearTryOnAlert();
          renderTryOnPage(tryOnState.currentPage);
          return;
        }

        if (tryOnSelected.size >= TRYON_LIMIT) {
          setTryOnAlert('試着点数は5点までです。');
          return;
        }

        tryOnSelected.add(item.id);
        clearTryOnAlert();
        renderTryOnPage(tryOnState.currentPage);
      });

      const img = document.createElement('img');
      img.className = 'tryon-thumb';
      img.src = item.image;
      img.alt = item.title;

      const text = document.createElement('div');
      text.className = 'tryon-text';
      const brand = document.createElement('div');
      brand.className = 'tryon-brand';
      brand.textContent = item.brand;
      const name = document.createElement('div');
      name.className = 'tryon-name';
      name.textContent = item.title;
      text.appendChild(brand);
      text.appendChild(name);

      const remove = document.createElement('button');
      remove.className = 'tryon-remove';
      remove.type = 'button';
      remove.textContent = '';
      remove.setAttribute('aria-label', '削除');
      remove.addEventListener('click', () => {
        trackClick('tryonlist_delete', item.id, {
          product_id: item.id,
          product_sku: item.sku ?? '',
        });
        tryOnList.delete(item.id);
        tryOnSelected.delete(item.id);
        clearTryOnAlert();
        renderTryOnPage(tryOnState.currentPage);
      });

      row.appendChild(check);
      row.appendChild(img);
      row.appendChild(text);
      row.appendChild(remove);
      listEl.appendChild(row);
    });
  }

  prevBtn.disabled = tryOnState.currentPage <= 0;
  nextBtn.disabled = tryOnState.currentPage >= totalPages - 1;

  dotsEl.innerHTML = '';
  for (let i = 0; i < totalPages; i++) {
    const dot = document.createElement('div');
    dot.className = 'slider-dot' + (i === tryOnState.currentPage ? ' active' : '');
    dotsEl.appendChild(dot);
  }

  countMainEl.textContent = String(tryOnSelected.size);
  countSubEl.textContent = `/${TRYON_LIMIT}着`;
  ctaEl.classList.toggle('is-empty', listed.length === 0);
};

const showTryOnModal = () => {
  if (!modalBackdrop || !modalBody) return;
  applyDefaultModalLayout();
  modalBody.innerHTML = '';

  setModalState('tryon');

  const header = document.createElement('div');
  header.className = 'tryon-header';
  const title = document.createElement('div');
  title.className = 'tryon-title';
  title.textContent = '試着リスト';
  const subtitle = document.createElement('div');
  subtitle.className = 'tryon-subtitle';
  subtitle.textContent = '5アイテムまで選択可能';
  header.appendChild(title);
  header.appendChild(subtitle);

  const alert = document.createElement('div');
  alert.className = 'tryon-alert';
  alert.style.display = 'none';

  const list = document.createElement('div');
  list.className = 'tryon-list';

  const controls = document.createElement('div');
  controls.className = 'slider-controls';

  const prevBtn = document.createElement('button');
  prevBtn.className = 'slider-arrow left';
  prevBtn.type = 'button';

  const dots = document.createElement('div');
  dots.className = 'slider-dots';

  const nextBtn = document.createElement('button');
  nextBtn.className = 'slider-arrow right';
  nextBtn.type = 'button';

  controls.appendChild(prevBtn);
  controls.appendChild(dots);
  controls.appendChild(nextBtn);

  const footer = document.createElement('div');
  footer.className = 'tryon-footer';
  const cta = document.createElement('button');
  cta.className = 'tryon-cta';
  cta.type = 'button';
  const count = document.createElement('span');
  count.className = 'tryon-count';
  const countMain = document.createElement('span');
  countMain.className = 'tryon-count-main';
  countMain.textContent = '0';
  const countSub = document.createElement('span');
  countSub.className = 'tryon-count-sub';
  countSub.textContent = '/5着';
  count.appendChild(countMain);
  count.appendChild(countSub);
  const label = document.createElement('span');
  label.textContent = '試着する';
  cta.appendChild(count);
  cta.appendChild(label);
  footer.appendChild(cta);

  cta.addEventListener('click', () => {
    if (tryOnSelected.size > TRYON_LIMIT) {
      setTryOnAlert('試着点数は5点までです。');
      return;
    }

    const skus = getSelectedSkus();
    if (skus === null) return;
    if (skus.length === 0) {
      setTryOnAlert('試着する商品を選択してください。');
      return;
    }

    clearTryOnAlert();
    trackClick('tryonlist_tryon', 'tryon', {
      selected_count: tryOnSelected.size,
      sku_list: skus.join(','),
    });
    openDigitalMirrorModal(skus);
  });

  modalBody.appendChild(header);
  modalBody.appendChild(alert);
  modalBody.appendChild(list);
  modalBody.appendChild(controls);
  modalBody.appendChild(footer);

  tryOnState = {
    currentPage: 0,
    totalPages: 0,
    listEl: list,
    dotsEl: dots,
    prevBtn,
    nextBtn,
    countEl: count,
    countMainEl: countMain,
    countSubEl: countSub,
    ctaEl: cta,
    alertEl: alert,
  };

  prevBtn.addEventListener('click', () => {
    if (tryOnState.currentPage <= 0) return;
    trackClick('tryonlist_pagenation', 'prev', {
      from_page: tryOnState.currentPage + 1,
      to_page: tryOnState.currentPage,
    });
    renderTryOnPage(tryOnState.currentPage - 1);
  });
  nextBtn.addEventListener('click', () => {
    if (tryOnState.currentPage >= tryOnState.totalPages - 1) return;
    trackClick('tryonlist_pagenation', 'next', {
      from_page: tryOnState.currentPage + 1,
      to_page: tryOnState.currentPage + 2,
    });
    renderTryOnPage(tryOnState.currentPage + 1);
  });

  renderTryOnPage(0);
  modalBackdrop.classList.add('visible');
};

// Open the try-on list modal from the floating icon.
// (Bind outside of the Showcase SDK connect flow so it still works even if SDK init fails.)
if (floatingList) {
  floatingList.addEventListener('click', () => {
    showTryOnModal();
  });
}

const openIframeModal = (url: string) => {
  if (!modalBackdrop || !modalBody) return;
  applyItemDetailLayout();
  modalBody.innerHTML = '';
  console.log('[ModalIframe] open request:', url);

  setModalState('ec_iframe', {
    tagSid: activeTagSid ?? undefined,
    productUrl: url,
  });

  const wrapper = document.createElement('div');
  wrapper.className = 'modal-iframe-wrapper mirror-tap-wrapper';

  const topBar = document.createElement('div');
  topBar.className = 'iframe-sheet-header';

  const spacer = document.createElement('div');
  spacer.className = 'iframe-sheet-spacer';

  const closeButton = document.createElement('button');
  closeButton.className = 'iframe-sheet-close';
  closeButton.type = 'button';
  closeButton.textContent = '閉じる';
  closeButton.addEventListener('click', () => {
    trackEcIframeClose('back');
    showProductModal();
  });
  topBar.appendChild(spacer);
  topBar.appendChild(closeButton);
  wrapper.appendChild(topBar);

  const iframe = document.createElement('iframe');
  iframe.className = 'modal-iframe mirror-tap-iframe';
  iframe.src = url;
  iframe.allowFullscreen = true;
  iframe.loading = 'lazy';
  iframe.addEventListener('load', () => {
    console.log('[ModalIframe] loaded:', url);
  });
  iframe.addEventListener('error', () => {
    console.error('[ModalIframe] failed to load:', url);
  });

  wrapper.appendChild(iframe);
  modalBody.appendChild(wrapper);
  modalBackdrop.classList.add('visible');
};

const openDigitalMirrorModal = (productSkus: string[], options: { source?: DigitalMirrorOpenSource } = {}) => {
  if (!modalBackdrop || !modalBody) return;

  const source = options.source ?? 'tryon';
  const useTapLikeLayout = source === 'tap' || currentModalType === 'tryon';
  const skus = Array.isArray(productSkus) ? productSkus.slice(0, TRYON_LIMIT) : [];
  if (useTapLikeLayout) {
    applyTapMirrorLayout();
  } else {
    applyDefaultModalLayout();
  }
  setModalState('plusmirror', { productSkus: skus });

  destroyDigitalMirrorClient();
  modalBody.innerHTML = '';

  const cfg = getDigitalMirrorConfigResult();
  if (!cfg.ok) {
    applyDefaultModalLayout();
    const userMessage = 'デジタルミラー設定が未設定です（digital-mirror.runtime-config.js を配置してください）';
    console.error('[DigitalMirror]', cfg.error);
    window.alert(userMessage);
    const msg = document.createElement('div');
    msg.style.padding = '16px';
    msg.style.color = '#b00020';
    msg.style.fontSize = '13px';
    msg.style.lineHeight = '1.6';
    msg.textContent = `${userMessage}\n${cfg.error}`;
    msg.style.whiteSpace = 'pre-line';
    modalBody.appendChild(msg);
    modalBackdrop.classList.add('visible');
    return;
  }

  const origin = cfg.config.origin;
  const auth = cfg.config.auth;
  const iframeSrc = `${origin}/iframe-initialize`;

  const wrapper = document.createElement('div');
  wrapper.className = useTapLikeLayout ? 'modal-iframe-wrapper mirror-tap-wrapper' : 'modal-iframe-wrapper';

  const closePlusMirrorInline = () => {
    trackPlusMirrorClose('x');
    destroyDigitalMirrorClient();
    clearModalState();
    hideModal();
  };

  if (useTapLikeLayout) {
    const topBar = document.createElement('div');
    topBar.className = 'mirror-tap-header';

    const logo = document.createElement('div');
    logo.className = 'mirror-tap-logo';
    const logoImg = document.createElement('img');
    logoImg.className = 'mirror-tap-logo-image';
    logoImg.src = './images/plus-mirror-logo.png';
    logoImg.alt = '+PLUS MIRROR';
    logoImg.addEventListener('error', () => {
      logo.innerHTML = '';
      const fallback = document.createElement('span');
      fallback.className = 'mirror-tap-logo-fallback';
      fallback.textContent = '+PLUS MIRROR';
      logo.appendChild(fallback);
    });
    logo.appendChild(logoImg);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'mirror-tap-close';
    closeBtn.type = 'button';
    closeBtn.textContent = '閉じる';
    closeBtn.addEventListener('click', closePlusMirrorInline);

    topBar.appendChild(logo);
    topBar.appendChild(closeBtn);
    wrapper.appendChild(topBar);
  }

  let statusPill: HTMLDivElement | null = null;
  if (!useTapLikeLayout) {
    const backButton = document.createElement('button');
    backButton.className = 'modal-back';
    backButton.type = 'button';
    backButton.textContent = '戻る';
    backButton.addEventListener('click', () => {
      trackPlusMirrorClose('back');
      destroyDigitalMirrorClient();
      showTryOnModal();
    });

    statusPill = document.createElement('div');
    statusPill.textContent = 'connecting...';
    statusPill.style.position = 'absolute';
    statusPill.style.top = '10px';
    statusPill.style.right = '10px';
    statusPill.style.zIndex = '2';
    statusPill.style.padding = '6px 10px';
    statusPill.style.borderRadius = '999px';
    statusPill.style.background = 'rgba(255, 255, 255, 0.9)';
    statusPill.style.border = '1px solid rgba(0, 0, 0, 0.12)';
    statusPill.style.fontSize = '12px';
    statusPill.style.fontWeight = '700';
    statusPill.style.color = '#444';

    wrapper.appendChild(backButton);
    wrapper.appendChild(statusPill);
  }

  const setStatus = (next: { text: string; kind?: 'ok' | 'warn' | 'error' }) => {
    if (!statusPill) {
      console.log('[DigitalMirror] status:', next.text);
      return;
    }
    statusPill.textContent = next.text;
    if (next.kind === 'ok') {
      statusPill.style.color = '#0f7a3a';
      statusPill.style.borderColor = 'rgba(15, 122, 58, 0.25)';
      return;
    }
    if (next.kind === 'error') {
      statusPill.style.color = '#b00020';
      statusPill.style.borderColor = 'rgba(176, 0, 32, 0.25)';
      return;
    }
    statusPill.style.color = '#444';
    statusPill.style.borderColor = 'rgba(0, 0, 0, 0.12)';
  };

  const iframe = document.createElement('iframe');
  iframe.className = useTapLikeLayout ? 'modal-iframe mirror-tap-iframe' : 'modal-iframe';
  iframe.src = iframeSrc;
  iframe.allowFullscreen = true;
  iframe.setAttribute('scrolling', 'yes');
  iframe.loading = 'eager';
  iframe.referrerPolicy = 'no-referrer';
  // Keep it broad for mirror apps (camera/fullscreen etc). Tighten later if needed.
  iframe.allow = 'camera; microphone; fullscreen; clipboard-read; clipboard-write';

  wrapper.appendChild(iframe);
  modalBody.appendChild(wrapper);
  modalBackdrop.classList.add('visible');

  const client = createDigitalMirrorClient({
    origin,
    auth,
    onStatus: (payload) => {
      if (payload.status === 'loading') {
        setStatus({ text: 'loading (auth...)', kind: 'warn' });
        return;
      }
      if (payload.status === 'ready') {
        setStatus({ text: 'ready', kind: 'ok' });
        return;
      }
      if (payload.status === 'error') {
        setStatus({ text: payload.message ? `error: ${payload.message}` : 'error', kind: 'error' });
        return;
      }
      setStatus({ text: payload.status });
    },
    onClose: (payload) => {
      console.log('[DigitalMirror] APP_CLOSE', payload);
      trackPlusMirrorClose('app');
      destroyDigitalMirrorClient();
      if (useTapLikeLayout) {
        clearModalState();
        hideModal();
      } else {
        showTryOnModal();
      }
    },
    onError: (err) => {
      console.warn('[DigitalMirror] error', err);
      setStatus({ text: `error: ${err.message}`, kind: 'error' });
    },
    debug: false,
  });

  digitalMirrorClient = client;
  client.mount(iframe);

  // Queue-safe (flushes after STATUS:ready)
  console.log('[DigitalMirror] PRODUCTS payload', { productIds: skus });
  client.sendProducts(skus);

  // Ask the child to respond with STATUS (optional)
  client.checkStatus();
};

const formatPrice = (price: number) => {
  return price.toLocaleString('ja-JP', { maximumFractionDigits: 0 });
};

const buildProductCard = (item: ProductItem) => {
  const card = document.createElement('div');
  card.className = 'product-card';

  const img = document.createElement('img');
  img.className = 'product-image';
  img.src = item.image;
  img.alt = item.title;
  card.appendChild(img);

  const content = document.createElement('div');
  content.className = 'product-content';

  const brand = document.createElement('div');
  brand.className = 'product-brand';
  brand.textContent = item.brand;
  content.appendChild(brand);

  const title = document.createElement('div');
  title.className = 'product-title';
  title.textContent = item.title;
  content.appendChild(title);

  const priceRow = document.createElement('div');
  priceRow.className = 'product-price-row';

  const price = document.createElement('div');
  price.className = 'product-price';
  price.textContent = formatPrice(item.price);
  priceRow.appendChild(price);

  const priceUnit = document.createElement('span');
  priceUnit.className = 'product-price-unit';
  priceUnit.textContent = '円';
  priceRow.appendChild(priceUnit);

  const note = document.createElement('div');
  note.className = 'product-note';
  note.textContent = '※参考価格';
  priceRow.appendChild(note);

  content.appendChild(priceRow);

  const actions = document.createElement('div');
  actions.className = 'product-actions';

  const cta = document.createElement('button');
  cta.className = 'product-cta';
  cta.type = 'button';
  cta.textContent = 'ITEM DETAIL';
  cta.addEventListener('click', () => {
    trackClick('tag_itemdetail', item.id, {
      tag_sid: activeTagSid,
      product_id: item.id,
      product_sku: item.sku ?? '',
      product_url: item.url,
    });

    if (ITEM_DETAIL_OPEN_IN_NEW_TAB) {
      window.open(item.url, '_blank', 'noopener,noreferrer');
      return;
    }

    // Legacy iframe mode (kept for future restore):
    // openIframeModal(item.url);
    openIframeModal(item.url);
  });
  actions.appendChild(cta);

  const secondary = document.createElement('button');
  secondary.className = 'product-secondary';
  secondary.type = 'button';
  const secondaryStatus = document.createElement('div');
  secondaryStatus.className = 'product-secondary-status';
  secondaryStatus.style.display = 'none';

  const updateSecondaryLabel = () => {
    secondary.textContent = tryOnList.has(item.id) ? '試着リストに追加しました' : '試着リストに追加';
  };
  updateSecondaryLabel();

  secondary.addEventListener('click', () => {
    const alreadyAdded = tryOnList.has(item.id);
    const blocked = !alreadyAdded && tryOnSelected.size >= TRYON_LIMIT;
    trackClick('tag_tryonlist', item.id, {
      tag_sid: activeTagSid,
      product_id: item.id,
      product_sku: item.sku ?? '',
      blocked,
    });

    if (alreadyAdded) {
      updateSecondaryLabel();
      secondaryStatus.textContent = '';
      secondaryStatus.style.display = 'none';
      secondaryStatus.classList.remove('warn');
      secondaryStatus.classList.remove('success');
      return;
    }

    tryOnList.add(item.id);

    if (blocked) {
      secondaryStatus.textContent = '';
      secondaryStatus.style.display = 'none';
      secondaryStatus.classList.remove('warn');
      secondaryStatus.classList.remove('success');
      updateSecondaryLabel();
      return;
    }

    tryOnSelected.add(item.id);
    secondaryStatus.textContent = '';
    secondaryStatus.style.display = 'none';
    secondaryStatus.classList.add('success');
    secondaryStatus.classList.remove('warn');
    clearTryOnAlert();
    updateSecondaryLabel();
  });
  actions.appendChild(secondary);
  actions.appendChild(secondaryStatus);

  content.appendChild(actions);

  card.appendChild(content);
  return card;
};

const renderSliderPage = (page: number) => {
  const { gridEl, dotsEl, prevBtn, nextBtn } = sliderState;
  if (!gridEl || !dotsEl || prevBtn === null || nextBtn === null) return;

  const start = page * PAGE_SIZE;
  const end = start + PAGE_SIZE;
  const slice = sliderProducts.slice(start, end);

  gridEl.innerHTML = '';
  slice.forEach((item) => {
    gridEl.appendChild(buildProductCard(item));
  });

  prevBtn.disabled = page <= 0;
  nextBtn.disabled = page >= sliderState.totalPages - 1;

  dotsEl.innerHTML = '';
  for (let i = 0; i < sliderState.totalPages; i++) {
    const dot = document.createElement('div');
    dot.className = 'slider-dot' + (i === page ? ' active' : '');
    dotsEl.appendChild(dot);
  }
};

const buildProductSlider = (container: HTMLElement) => {
  const headerRow = document.createElement('div');
  headerRow.className = 'modal-header-row';

  const title = document.createElement('div');
  title.className = 'modal-title';
  title.textContent = 'ITEM LIST';
  headerRow.appendChild(title);

  const grid = document.createElement('div');
  grid.className = 'product-grid';

  const controls = document.createElement('div');
  controls.className = 'slider-controls';

  const prevBtn = document.createElement('button');
  prevBtn.className = 'slider-arrow left';
  prevBtn.type = 'button';
  prevBtn.innerHTML = '';

  const dots = document.createElement('div');
  dots.className = 'slider-dots';

  const nextBtn = document.createElement('button');
  nextBtn.className = 'slider-arrow right';
  nextBtn.type = 'button';
  nextBtn.innerHTML = '';

  controls.appendChild(prevBtn);
  controls.appendChild(dots);
  controls.appendChild(nextBtn);

  container.appendChild(headerRow);
  container.appendChild(grid);
  container.appendChild(controls);

  sliderState = {
    currentPage: 0,
    totalPages: Math.max(1, Math.ceil(sliderProducts.length / PAGE_SIZE)),
    gridEl: grid,
    dotsEl: dots,
    prevBtn,
    nextBtn,
  };

  prevBtn.addEventListener('click', () => {
    if (sliderState.currentPage <= 0) return;

    trackClick('tag_pagenation', 'prev', {
      tag_sid: activeTagSid,
      from_page: sliderState.currentPage + 1,
      to_page: sliderState.currentPage,
    });

    sliderState.currentPage -= 1;
    renderSliderPage(sliderState.currentPage);
  });

  nextBtn.addEventListener('click', () => {
    if (sliderState.currentPage >= sliderState.totalPages - 1) return;

    trackClick('tag_pagenation', 'next', {
      tag_sid: activeTagSid,
      from_page: sliderState.currentPage + 1,
      to_page: sliderState.currentPage + 2,
    });

    sliderState.currentPage += 1;
    renderSliderPage(sliderState.currentPage);
  });

  renderSliderPage(sliderState.currentPage);
};

const logMattertagTitleAndIdList = async (sdk: MpSdk.CommonMpSdk): Promise<void> => {
  const sdkAny = sdk as any;

  if (sdkAny.Tag?.data?.waitUntil) {
    let collection: Record<string, any> = {};

    try {
      // Prefer waiting until tags are populated, but fallback quickly when there are no tags.
      collection = (await Promise.race([
        sdkAny.Tag.data.waitUntil((current: Record<string, any>) => Object.keys(current ?? {}).length > 0),
        new Promise<Record<string, any>>((resolve) => window.setTimeout(() => resolve({}), 3000)),
      ])) as Record<string, any>;

      // Get the latest snapshot if the timeout won the race.
      if (Object.keys(collection).length === 0) {
        collection = (await sdkAny.Tag.data.waitUntil(() => true)) as Record<string, any>;
      }
    } catch (e) {
      console.warn('[Mattertag] Failed to read Tag.data collection', e);
      collection = {};
    }

    const rows = Object.values(collection ?? {})
      .map((item: any) => ({
        title: String(item?.label ?? '').trim(),
        tagId: String(item?.id ?? '').trim(),
      }))
      .filter((row: { title: string; tagId: string }) => row.tagId.length > 0);

    if (rows.length === 0) {
      console.log('[Mattertag] タイトル / タグID 一覧: 0件');
      return;
    }

    console.log(`[Mattertag] タイトル / タグID 一覧 (${rows.length}件)`);
    console.table(rows);
    return;
  }

  // Legacy fallback
  if (sdkAny.Mattertag?.getData) {
    try {
      const items = (await sdkAny.Mattertag.getData()) as Array<{ sid?: string; label?: string }>;
      const rows = Array.isArray(items)
        ? items
            .map((item) => ({
              title: String(item?.label ?? '').trim(),
              tagId: String(item?.sid ?? '').trim(),
            }))
            .filter((row) => row.tagId.length > 0)
        : [];

      if (rows.length === 0) {
        console.log('[Mattertag] タイトル / タグID 一覧: 0件');
        return;
      }

      console.log(`[Mattertag] タイトル / タグID 一覧 (${rows.length}件)`);
      console.table(rows);
    } catch (e) {
      console.warn('[Mattertag] Failed to read Mattertag.getData()', e);
    }
  }
};

// declare this file is a module
export {};

// augment window with the MP_SDK property
declare global {
  interface Window {
    MP_SDK: any;
  }
}

type Disposable = { dispose(): void };

const waitFor = async (fn: () => boolean, timeoutMs = 8000, intervalMs = 50) => {
  const start = Date.now();
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (fn()) return;
    if (Date.now() - start > timeoutMs) throw new Error('Timed out');
    await new Promise((r) => setTimeout(r, intervalMs));
  }
};

class TapSignComponent implements MpSdk.Scene.IComponent {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  inputs = {
    url: TAP_SIGN_TEXTURE_PATH_IN_SHOWCASE,
    visible: true,
    position: { ...TAP_SIGN_POSITION },
    orientation: { ...TAP_SIGN_ORIENTATION },
    diameter: TAP_SIGN_DIAMETER_METERS,
    billboard: TAP_SIGN_BILLBOARD,
  } as Record<string, unknown>;

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  // IMPORTANT:
  // `outputs.objectRoot` / `outputs.collider` are reserved and injected by the SDK.
  // If we define them here, the SDK throws:
  //   "outputs.objectRoot is a reserved property of the outputs object"
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  outputs = {} as MpSdk.Scene.PredefinedOutputs & Record<string, unknown>;

  // Handle click interactions from the 3D collider.
  events = { 'INTERACTION.CLICK': true } as Record<string, boolean>;
  emits = {} as Record<string, boolean>;
  context!: MpSdk.Scene.IComponentContext;

  private container: any | null = null;
  private mesh: any | null = null;
  private geometry: any | null = null;
  private material: any | null = null;
  private texture: any | null = null;
  private isAnimatedGifTexture = false;
  private gifImageEl: HTMLImageElement | null = null;
  private gifCanvasEl: HTMLCanvasElement | null = null;
  private gifCanvasCtx: CanvasRenderingContext2D | null = null;
  private gifFrameBitmaps: ImageBitmap[] = [];
  private gifFrameDelaysMs: number[] = [];
  private gifFrameIndex = 0;
  private gifFrameElapsedMs = 0;
  private gifUsesDecodedFrames = false;
  private gifRafId: number | null = null;
  private urlVersion = 0;
  private tmpV3: any | null = null;

  private getShowcaseTHREE() {
    return (showcase.contentWindow as any)?.THREE;
  }

  private getShowcaseDocument() {
    return showcase.contentWindow?.document ?? null;
  }

  private resolveShowcaseAssetUrl(rawUrl: string): string {
    const u = String(rawUrl ?? '').trim();
    if (!u) return '';
    try {
      const base = showcase.contentWindow?.location?.href ?? window.location.href;
      return new URL(u, base).toString();
    } catch {
      return u;
    }
  }

  private cleanupGifImage() {
    if (!this.gifImageEl) return;
    try {
      this.gifImageEl.onload = null;
      this.gifImageEl.onerror = null;
      this.gifImageEl.remove();
    } catch {}
    this.gifImageEl = null;
  }

  private cleanupDecodedGifFrames() {
    this.gifFrameBitmaps.forEach((bitmap) => {
      try {
        bitmap.close();
      } catch {}
    });
    this.gifFrameBitmaps = [];
    this.gifFrameDelaysMs = [];
    this.gifFrameIndex = 0;
    this.gifFrameElapsedMs = 0;
    this.gifUsesDecodedFrames = false;
    this.gifCanvasCtx = null;
    this.gifCanvasEl = null;
  }

  private stopGifTextureLoop() {
    if (this.gifRafId === null) return;
    try {
      const win = showcase.contentWindow ?? window;
      win.cancelAnimationFrame(this.gifRafId);
    } catch {}
    this.gifRafId = null;
  }

  private startGifTextureLoop() {
    this.stopGifTextureLoop();
    if (!this.isAnimatedGifTexture || this.gifUsesDecodedFrames) return;

    const win = showcase.contentWindow ?? window;
    const loop = () => {
      if (this.isAnimatedGifTexture && this.texture) {
        this.texture.needsUpdate = true;
        this.gifRafId = win.requestAnimationFrame(loop);
        return;
      }
      this.gifRafId = null;
    };

    this.gifRafId = win.requestAnimationFrame(loop);
  }

  private async createGifTextureFromImageDecoder(url: string, THREE: any): Promise<any | null> {
    const DecoderCtor = ((showcase.contentWindow as any)?.ImageDecoder ?? (window as any).ImageDecoder) as any;
    const createImageBitmapFn =
      ((showcase.contentWindow as any)?.createImageBitmap ?? (window as any).createImageBitmap) as any;
    const doc = this.getShowcaseDocument();
    if (!DecoderCtor || typeof createImageBitmapFn !== 'function' || !doc) return null;

    try {
      const resolvedUrl = this.resolveShowcaseAssetUrl(url);
      const res = await fetch(resolvedUrl, { credentials: 'same-origin' });
      if (!res.ok) return null;

      const bytes = await res.arrayBuffer();
      const decoder = new DecoderCtor({ data: bytes, type: 'image/gif' });
      if (decoder?.tracks?.ready) {
        await decoder.tracks.ready;
      }

      const selectedTrack = decoder?.tracks?.selectedTrack;
      const frameCountRaw = Number(selectedTrack?.frameCount ?? 0);
      const frameCount =
        Number.isFinite(frameCountRaw) && frameCountRaw > 0 ? Math.min(300, Math.floor(frameCountRaw)) : 0;
      if (frameCount <= 0) {
        if (typeof decoder.close === 'function') decoder.close();
        return null;
      }

      const bitmaps: ImageBitmap[] = [];
      const delays: number[] = [];

      for (let i = 0; i < frameCount; i++) {
        const decoded = await decoder.decode({ frameIndex: i });
        const frame = decoded?.image;
        if (!frame) continue;

        const bitmap = await createImageBitmapFn(frame);
        const durationUs = Number((frame as any).duration ?? 40000);
        const delayMs =
          Number.isFinite(durationUs) && durationUs > 0 ? Math.max(16, Math.round(durationUs / 1000)) : 40;

        bitmaps.push(bitmap);
        delays.push(delayMs);

        if (typeof frame.close === 'function') {
          frame.close();
        }
      }

      if (typeof decoder.close === 'function') decoder.close();
      if (bitmaps.length === 0) return null;

      const canvas = doc.createElement('canvas');
      canvas.width = Math.max(1, Number(bitmaps[0].width) || 1);
      canvas.height = Math.max(1, Number(bitmaps[0].height) || 1);
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        bitmaps.forEach((bitmap) => {
          try {
            bitmap.close();
          } catch {}
        });
        return null;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(bitmaps[0], 0, 0, canvas.width, canvas.height);

      this.gifCanvasEl = canvas;
      this.gifCanvasCtx = ctx;
      this.gifFrameBitmaps = bitmaps;
      this.gifFrameDelaysMs = delays;
      this.gifFrameIndex = 0;
      this.gifFrameElapsedMs = 0;
      this.gifUsesDecodedFrames = bitmaps.length >= 2;

      const texture = new THREE.CanvasTexture(canvas);
      texture.needsUpdate = true;
      texture.generateMipmaps = false;
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.wrapS = THREE.ClampToEdgeWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;
      return texture;
    } catch (e) {
      console.warn('[TapSign] ImageDecoder GIF path failed', e);
      return null;
    }
  }

  private createGifTextureFromImage(url: string, THREE: any): Promise<any | null> {
    const doc = this.getShowcaseDocument();
    if (!doc) return Promise.resolve(null);

    const img = doc.createElement('img');
    img.alt = '';
    img.setAttribute('aria-hidden', 'true');
    img.decoding = 'async';
    img.style.position = 'fixed';
    img.style.left = '0';
    img.style.top = '0';
    img.style.width = '1px';
    img.style.height = '1px';
    img.style.opacity = '0.001';
    img.style.pointerEvents = 'none';
    img.style.zIndex = '1';
    doc.body?.appendChild(img);

    this.gifImageEl = img;

    return new Promise<any>((resolve) => {
      img.onload = () => {
        try {
          const texture = new THREE.Texture(img);
          texture.needsUpdate = true;
          texture.generateMipmaps = false;
          texture.minFilter = THREE.LinearFilter;
          texture.magFilter = THREE.LinearFilter;
          texture.wrapS = THREE.ClampToEdgeWrapping;
          texture.wrapT = THREE.ClampToEdgeWrapping;
          resolve(texture);
        } catch (e) {
          console.warn('[TapSign] Failed to create GIF texture from image element', e);
          resolve(null);
        }
      };
      img.onerror = () => {
        resolve(null);
      };
      img.src = this.resolveShowcaseAssetUrl(url);
    });
  }

  private advanceDecodedGifFrame(tickDeltaMs: number) {
    if (!this.gifUsesDecodedFrames) return;
    if (!this.texture || !this.gifCanvasEl || !this.gifCanvasCtx) return;
    if (this.gifFrameBitmaps.length === 0) return;

    const delta = Number.isFinite(tickDeltaMs) ? Math.max(0, tickDeltaMs) : 16;
    this.gifFrameElapsedMs += delta;

    let nextDelay = this.gifFrameDelaysMs[this.gifFrameIndex] ?? 40;
    let safety = 0;
    while (this.gifFrameElapsedMs >= nextDelay && safety < this.gifFrameBitmaps.length + 1) {
      this.gifFrameElapsedMs -= nextDelay;
      this.gifFrameIndex = (this.gifFrameIndex + 1) % this.gifFrameBitmaps.length;
      nextDelay = this.gifFrameDelaysMs[this.gifFrameIndex] ?? 40;
      safety++;
    }

    const frame = this.gifFrameBitmaps[this.gifFrameIndex];
    if (!frame) return;
    this.gifCanvasCtx.clearRect(0, 0, this.gifCanvasEl.width, this.gifCanvasEl.height);
    this.gifCanvasCtx.drawImage(frame, 0, 0, this.gifCanvasEl.width, this.gifCanvasEl.height);
    this.texture.needsUpdate = true;
  }

  private applyTransforms() {
    const THREE = this.getShowcaseTHREE();
    if (!THREE || !this.container) return;

    const position = (this.inputs.position as any) ?? {};
    const orientation = (this.inputs.orientation as any) ?? {};

    const yaw = Number(orientation.yaw ?? 0);
    const pitch = Number(orientation.pitch ?? 0);
    const roll = Number(orientation.roll ?? 0);

    const diameter = Number((this.inputs as any).diameter ?? 1);
    const d = Number.isFinite(diameter) && diameter > 0 ? diameter : 1;

    this.container.visible = Boolean(this.inputs.visible);
    this.container.position.set(Number(position.x ?? 0), Number(position.y ?? 0), Number(position.z ?? 0));
    this.container.scale.set(d, d, d); // geometry has diameter=1, so scale==diameter in meters

    // If billboard is enabled we set rotation in onTick (yaw-only).
    if (!Boolean((this.inputs as any).billboard)) {
      this.container.rotation.set(
        THREE.MathUtils.degToRad(pitch),
        THREE.MathUtils.degToRad(yaw),
        THREE.MathUtils.degToRad(roll),
        'YXZ'
      );
    }
  }

  private async loadTexture() {
    const THREE = this.getShowcaseTHREE();
    const inputUrl = String((this.inputs as any).url ?? '').trim();
    if (!THREE?.TextureLoader) return;
    const candidates = Array.from(
      new Set(
        [
          inputUrl,
          TAP_SIGN_TEXTURE_PRIMARY_PATH_IN_SHOWCASE,
          TAP_SIGN_TEXTURE_FALLBACK_PATH_IN_SHOWCASE,
        ].filter((v) => Boolean(v))
      )
    ) as string[];
    if (candidates.length === 0) return;

    const myVersion = ++this.urlVersion;
    const loader = new THREE.TextureLoader();
    let texture: any | null = null;
    let loadedUrl = '';
    let loadedAsGif = false;
    this.stopGifTextureLoop();
    this.cleanupGifImage();
    this.cleanupDecodedGifFrames();
    for (const url of candidates) {
      const isGif = /\.gif(?:$|[?#])/i.test(url);
      if (isGif) {
        texture = await this.createGifTextureFromImageDecoder(url, THREE);
        if (!texture) {
          texture = await this.createGifTextureFromImage(url, THREE);
        }
      } else {
        const resolvedUrl = this.resolveShowcaseAssetUrl(url);
        texture = await new Promise<any>((resolve) => {
          loader.load(
            resolvedUrl,
            (loadedTexture: any) => resolve(loadedTexture),
            undefined,
            (e: unknown) => {
              console.warn('[TapSign] Failed to load texture', url, e);
              resolve(null);
            }
          );
        });
      }
      if (texture) {
        loadedUrl = url;
        loadedAsGif = isGif;
        break;
      }
      if (isGif) {
        this.cleanupGifImage();
        this.cleanupDecodedGifFrames();
      }
      console.warn('[TapSign] Failed to load texture', url);
    }
    if (!texture) return;
    if (myVersion !== this.urlVersion) {
      try {
        texture.dispose?.();
      } catch {}
      if (loadedAsGif) {
        this.cleanupGifImage();
        this.cleanupDecodedGifFrames();
      }
      return;
    }

    // Replace previous
    try {
      this.texture?.dispose?.();
    } catch {}
    this.texture = texture;
    this.isAnimatedGifTexture = loadedAsGif || /\.gif(?:$|[?#])/i.test(loadedUrl);
    if (this.isAnimatedGifTexture) {
      console.log(
        '[TapSign] GIF mode:',
        this.gifUsesDecodedFrames ? 'ImageDecoder (frame-by-frame)' : 'Image element fallback'
      );
    }
    if (this.isAnimatedGifTexture) this.startGifTextureLoop();

    // Keep colors faithful for UI-like SVG/PNG textures
    if ('colorSpace' in texture && THREE.SRGBColorSpace) {
      try {
        texture.colorSpace = THREE.SRGBColorSpace;
      } catch {}
    }

    if (this.material) {
      this.material.map = texture;
      this.material.needsUpdate = true;
    }
  }

  onInit() {
    const THREE = this.getShowcaseTHREE();
    if (!THREE) return;

    this.tmpV3 = new THREE.Vector3();

    // Circle geometry with diameter=1 (we scale it in applyTransforms)
    this.geometry = new THREE.CircleGeometry(0.5, 64);
    this.material = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 1,
      side: THREE.DoubleSide,
      depthTest: true,
    });

    this.mesh = new THREE.Mesh(this.geometry, this.material);

    this.container = new THREE.Group();
    this.container.add(this.mesh);

    // Attach to SDK scene graph and enable interactions
    (this.outputs as any).objectRoot = this.container;
    (this.outputs as any).collider = this.mesh;

    this.applyTransforms();
    this.loadTexture().catch((e) => console.warn('[TapSign] loadTexture failed', e));
  }

  onInputsUpdated(previousInputs: Record<string, unknown>) {
    const prevUrl = String((previousInputs as any)?.url ?? '');
    const nextUrl = String((this.inputs as any)?.url ?? '');
    if (prevUrl !== nextUrl) {
      this.loadTexture().catch((e) => console.warn('[TapSign] reload texture failed', e));
    }
    this.applyTransforms();
  }

  onTick(tickDeltaMs?: number) {
    if (this.isAnimatedGifTexture && this.gifUsesDecodedFrames) {
      this.advanceDecodedGifFrame(Number(tickDeltaMs ?? 16));
    }

    if (!Boolean((this.inputs as any).billboard)) return;
    if (!this.container) return;

    const THREE = this.getShowcaseTHREE();
    if (!THREE || !this.context?.camera) return;

    if (!this.tmpV3) this.tmpV3 = new THREE.Vector3();
    this.context.camera.getWorldPosition(this.tmpV3);

    const p = this.container.position;
    const dx = this.tmpV3.x - p.x;
    const dz = this.tmpV3.z - p.z;
    const yaw = Math.atan2(dx, dz);
    this.container.rotation.set(0, yaw, 0);
  }

  onEvent(eventType: string, eventData?: unknown) {
    if (String(eventType) !== 'INTERACTION.CLICK') return;
    console.log('[TapSign] clicked', eventData);
    // Launch Digital Mirror directly (same destination as "試着する").
    const skus = Array.from(
      new Set(
        getSelectedProducts()
          .map((item) => String(item.sku ?? '').trim())
          .filter(Boolean)
      )
    );
    openDigitalMirrorModal(skus, { source: 'tap' });
  }

  onDestroy() {
    this.urlVersion++;
    this.stopGifTextureLoop();

    try {
      this.texture?.dispose?.();
    } catch {}
    this.texture = null;
    this.cleanupGifImage();
    this.cleanupDecodedGifFrames();
    this.isAnimatedGifTexture = false;

    try {
      this.material?.dispose?.();
    } catch {}
    this.material = null;

    try {
      this.geometry?.dispose?.();
    } catch {}
    this.geometry = null;

    this.mesh = null;
    this.container = null;

    if (this.outputs) {
      (this.outputs as any).objectRoot = null;
      (this.outputs as any).collider = null;
    }
  }
}

class AnimatedGlbComponent implements MpSdk.Scene.IComponent {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  inputs = {
    url: CG_GLB_PATH_IN_SHOWCASE,
    visible: true,
    position: { ...CG_GLB_POSITION },
    orientation: { ...CG_GLB_ORIENTATION },
    scale: { ...CG_GLB_SCALE },
    autoFitHeight: CG_GLB_AUTO_FIT_HEIGHT,
    fitHeightMeters: CG_GLB_FIT_HEIGHT_METERS,
    // If the GLB has no embedded animations, fall back to a slow spin so you can confirm it's "moving".
    fallbackSpin: true,
    fallbackSpinDegPerSec: 30,
  } as Record<string, unknown>;

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  // IMPORTANT:
  // `outputs.objectRoot` / `outputs.collider` are reserved and injected by the SDK.
  // If we define them here, the SDK throws:
  //   "outputs.objectRoot is a reserved property of the outputs object"
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  outputs = {} as MpSdk.Scene.PredefinedOutputs & Record<string, unknown>;

  events = {} as Record<string, boolean>;
  emits = {} as Record<string, boolean>;
  context!: MpSdk.Scene.IComponentContext;

  private container: any | null = null;
  private modelRoot: any | null = null;
  private mixer: any | null = null;
  private urlVersion = 0;
  private autoScale = 1;

  private getShowcaseTHREE() {
    return (showcase.contentWindow as any)?.THREE;
  }

  // Force GLB/GLTF materials to remain visible even when the scene lacks sufficient lighting/IBL.
  // This intentionally trades physical correctness for "always show the texture colors".
  private fixGlbMaterials(root: any) {
    const THREE = this.getShowcaseTHREE();
    if (!THREE || !root?.traverse) return;

    const setSrgb = (tex: any) => {
      if (!tex) return;
      try {
        if (THREE.SRGBColorSpace && 'colorSpace' in tex) {
          tex.colorSpace = THREE.SRGBColorSpace;
        }
      } catch {}
      try {
        tex.needsUpdate = true;
      } catch {}
    };

    const fixOne = (mat: any) => {
      if (!mat) return;

      // remove dark tint
      if (mat.color?.set) mat.color.set(0xffffff);

      // ensure sRGB for baseColor/emissive maps
      if (mat.map) setSrgb(mat.map);

      // make textures visible even without good lighting/IBL
      if (mat.emissive?.set) {
        mat.emissive.set(0xffffff);
        if (mat.map && !mat.emissiveMap) mat.emissiveMap = mat.map;
        if (mat.emissiveMap) setSrgb(mat.emissiveMap);
        mat.emissiveIntensity = 1.0;
      }

      // avoid black from PBR features when no envMap
      if (typeof mat.metalness === 'number') mat.metalness = 0;
      if (typeof mat.roughness === 'number') mat.roughness = 1;
      if (typeof mat.transmission === 'number') mat.transmission = 0;
      if (typeof mat.clearcoat === 'number') mat.clearcoat = 0;
      if (typeof mat.specularIntensity === 'number') mat.specularIntensity = 0;
      if (typeof mat.iridescence === 'number') mat.iridescence = 0;

      mat.needsUpdate = true;
    };

    root.traverse((obj: any) => {
      const m = obj?.material;
      if (!m) return;
      if (Array.isArray(m)) m.forEach(fixOne);
      else fixOne(m);
    });
  }

  private applyTransforms() {
    if (!this.container) return;
    const THREE = this.getShowcaseTHREE();
    if (!THREE) return;

    const position = (this.inputs.position as any) ?? {};
    const orientation = (this.inputs.orientation as any) ?? {};
    const scale = (this.inputs.scale as any) ?? {};

    const yaw = Number(orientation.yaw ?? 0);
    const pitch = Number(orientation.pitch ?? 0);
    const roll = Number(orientation.roll ?? 0);

    this.container.visible = Boolean(this.inputs.visible);
    this.container.position.set(Number(position.x ?? 0), Number(position.y ?? 0), Number(position.z ?? 0));
    this.container.rotation.set(
      THREE.MathUtils.degToRad(pitch),
      THREE.MathUtils.degToRad(yaw),
      THREE.MathUtils.degToRad(roll),
      'YXZ'
    );
    this.container.scale.set(
      Number(scale.x ?? 1) * this.autoScale,
      Number(scale.y ?? 1) * this.autoScale,
      Number(scale.z ?? 1) * this.autoScale
    );
  }

  private async load() {
    const THREE = this.getShowcaseTHREE();
    const url = String(this.inputs.url ?? '');
    if (!url) {
      console.warn('[CG] url is empty, skipping model load');
      return;
    }

    const cleanUrl = url.split('#')[0].split('?')[0];
    const ext = String(cleanUrl.split('.').pop() ?? '').toLowerCase();
    const isFbx = ext === 'fbx';

    if (isFbx) {
      if (!THREE?.FBXLoader) {
        console.error('[CG] THREE.FBXLoader is not available. Check bundle/showcase.html loader injection.');
        return;
      }
    } else {
      if (!THREE?.GLTFLoader) {
        console.error('[CG] THREE.GLTFLoader is not available. Check bundle/showcase.html loader injection.');
        return;
      }
    }

    const myVersion = ++this.urlVersion;

    // Reset old scene/mixer
    this.mixer = null;
    this.container = null;
    this.modelRoot = null;
    if (this.outputs) {
      (this.outputs as any).objectRoot = null;
      (this.outputs as any).collider = null;
    }

    let root: any | null = null;
    let animations: any[] = [];

    if (isFbx) {
      const loader = new THREE.FBXLoader();
      const fbx = await new Promise<any>((resolve, reject) => {
        loader.load(url, resolve, undefined, reject);
      }).catch((e) => {
        console.error('[CG] Failed to load FBX', url, e);
        return null;
      });
      if (!fbx) return;
      if (myVersion !== this.urlVersion) return; // URL changed while loading

      root = fbx;
      animations = Array.isArray(fbx.animations) ? fbx.animations : [];
    } else {
      const loader = new THREE.GLTFLoader();
      const gltf = await new Promise<any>((resolve, reject) => {
        loader.load(url, resolve, undefined, reject);
      }).catch((e) => {
        console.error('[CG] Failed to load GLB/GLTF', url, e);
        return null;
      });
      if (!gltf) return;
      if (myVersion !== this.urlVersion) return; // URL changed while loading

      root = gltf.scene ?? gltf.scenes?.[0] ?? null;
      if (!root) {
        console.error('[CG] GLB/GLTF loaded but scene root is missing', url);
        return;
      }
      animations = Array.isArray(gltf.animations) ? gltf.animations : [];
    }

    this.modelRoot = root;
    if (!isFbx) {
      try {
        this.fixGlbMaterials(root);
        console.log('[CG] Applied GLB material fixes (emissive/PBR overrides)');
      } catch (e) {
        console.warn('[CG] Failed to apply GLB material fixes', e);
      }
    }

    // Wrap the model under a container so we can freely move/scale/rotate it
    // without clobbering any transforms baked into the GLB root.
    const container = new THREE.Group();
    container.add(root);
    this.container = container;

    // If the scene has no lights, glTF's PBR materials (MeshStandard/Physical) will render black.
    // Add basic lights near the model, or optionally force-unlit materials as a debug fallback.
    if (Boolean((this.inputs as any).forceUnlitMaterials)) {
      try {
        root.traverse((obj: any) => {
          if (!obj?.isMesh) return;

          const toBasic = (m: any) => {
            const baseMap = m?.map ?? null;
            const emissiveMap = m?.emissiveMap ?? null;
            const map = baseMap ?? emissiveMap ?? null;

            const opacity = Number.isFinite(m?.opacity) ? Number(m.opacity) : 1;
            const transparent = Boolean(m?.transparent) || opacity < 1;
            const alphaTest = Number.isFinite(m?.alphaTest) ? Number(m.alphaTest) : 0;

            // If we have a texture, force tint to white so it isn't darkened by baseColorFactor.
            // If we don't have a texture, preserve the original color when available.
            const color = new THREE.Color(0xffffff);
            if (!map && m?.color?.isColor) {
              color.copy(m.color);
            } else {
              color.set(0xffffff);
            }

            const basic = new THREE.MeshBasicMaterial({
              color,
              map,
              // Keep transparency behavior only when needed. Some render pipelines treat
              // fully-transparent materials differently (and may cull them).
              transparent,
              opacity,
              alphaTest,
              // Force double-sided so the model remains visible even if the camera ends up inside it.
              side: THREE.DoubleSide,
              depthTest: m?.depthTest,
              depthWrite: m?.depthWrite,
              vertexColors: Boolean(m?.vertexColors),
            });
            // Avoid tone-mapping to keep UI-like texture colors intact.
            (basic as any).toneMapped = false;

            // Ensure proper sRGB sampling for UI-like base color textures.
            if (map && 'colorSpace' in map && THREE.SRGBColorSpace) {
              try {
                (map as any).colorSpace = THREE.SRGBColorSpace;
              } catch {}
              try {
                (map as any).needsUpdate = true;
              } catch {}
            }

            // Some models put their "real" colors into emissive textures, with a dark baseColor.
            // Approximate the original look by adding emissiveMap on top of baseColor in an unlit shader patch.
            if (baseMap && emissiveMap) {
              if ('colorSpace' in emissiveMap && THREE.SRGBColorSpace) {
                try {
                  (emissiveMap as any).colorSpace = THREE.SRGBColorSpace;
                } catch {}
                try {
                  (emissiveMap as any).needsUpdate = true;
                } catch {}
              }

              const emissiveFactor = new THREE.Color(0xffffff);
              if (m?.emissive?.isColor) {
                emissiveFactor.copy(m.emissive);
              }
              const intensity = Number.isFinite(m?.emissiveIntensity) ? Number(m.emissiveIntensity) : 1;
              emissiveFactor.multiplyScalar(intensity);

              basic.onBeforeCompile = (shader: any) => {
                shader.uniforms.emissiveMap = { value: emissiveMap };
                shader.uniforms.emissiveFactor = { value: emissiveFactor };

                shader.fragmentShader = shader.fragmentShader
                  .replace(
                    '#include <common>',
                    '#include <common>\nuniform sampler2D emissiveMap;\nuniform vec3 emissiveFactor;'
                  )
                  .replace(
                    '#include <map_fragment>',
                    [
                      '#include <map_fragment>',
                      // vMapUv is available because baseMap exists.
                      'vec3 _em = texture2D( emissiveMap, vMapUv ).rgb * emissiveFactor;',
                      'diffuseColor.rgb += _em;',
                    ].join('\n')
                  );
              };
              (basic as any).customProgramCacheKey = () => 'MeshBasicMaterial+emissiveOverlay';
              basic.needsUpdate = true;
            }
            // Needed for skinned meshes.
            (basic as any).skinning = Boolean(m?.skinning);
            (basic as any).morphTargets = Boolean(m?.morphTargets);
            (basic as any).morphNormals = Boolean(m?.morphNormals);
            return basic;
          };

          const mat = obj.material;
          obj.material = Array.isArray(mat) ? mat.map(toBasic) : toBasic(mat);
        });
        console.log('[CG] Applied unlit (MeshBasicMaterial) to model materials');
      } catch (e) {
        console.warn('[CG] Failed to force unlit materials', e);
      }
    } else if (Boolean((this.inputs as any).addLights)) {
      try {
        const ambient = new THREE.AmbientLight(
          0xffffff,
          Number((this.inputs as any).ambientLightIntensity ?? 1.0)
        );
        const dir = new THREE.DirectionalLight(
          0xffffff,
          Number((this.inputs as any).directionalLightIntensity ?? 1.0)
        );
        dir.position.set(2, 5, 2);
        // Keep direction stable by setting a target in the same space.
        dir.target.position.set(0, 1, 0);
        container.add(ambient);
        container.add(dir);
        container.add(dir.target);
      } catch (e) {
        console.warn('[CG] Failed to add lights', e);
      }
    }

    // Optional: ground the model so its lowest point sits on Y=0 (local).
    // This avoids it being half-buried when we place it on the floor.
    try {
      container.updateMatrixWorld(true);
      const box = new THREE.Box3().setFromObject(root);
      if (Number.isFinite(box.min.y) && box.min.y !== 0) {
        root.position.y -= box.min.y;
        root.updateMatrixWorld(true);
      }
    } catch (e) {
      console.warn('[CG] Failed to ground model', e);
    }

    // Auto scale by height if enabled (this helps when GLB scale units are unexpected).
    this.autoScale = 1;
    if (this.inputs.autoFitHeight) {
      const box = new THREE.Box3().setFromObject(root);
      const size = box.getSize(new THREE.Vector3());
      const h = Number(size.y);
      const target = Number(this.inputs.fitHeightMeters ?? 0);
      if (Number.isFinite(h) && h > 0 && Number.isFinite(target) && target > 0) {
        this.autoScale = target / h;
      }
    }
    this.applyTransforms();

    if (animations.length > 0) {
      this.mixer = new THREE.AnimationMixer(root);
      animations.forEach((clip: any) => {
        const action = this.mixer.clipAction(clip);
        action.play();
      });
      console.log('[CG] Model loaded (animations:', animations.length + ')', url);
    } else {
      console.log('[CG] Model loaded (no animations)', url);
    }

    // Attach to the SDK scene graph
    (this.outputs as any).objectRoot = container;
    (this.outputs as any).collider = container;
  }

  onInit() {
    this.load().catch((e) => console.error('[CG] init failed', e));
  }

  onInputsUpdated(previousInputs: Record<string, unknown>) {
    const prevUrl = String((previousInputs as any)?.url ?? '');
    const nextUrl = String((this.inputs as any)?.url ?? '');
    if (prevUrl !== nextUrl) {
      this.load().catch((e) => console.error('[CG] reload failed', e));
      return;
    }
    this.applyTransforms();
  }

  onTick(tickDeltaMs: number) {
    const THREE = this.getShowcaseTHREE();
    if (!THREE) return;

    // Keep visibility responsive even if input change detection is delayed.
    if (this.container) {
      this.container.visible = Boolean((this.inputs as any).visible);
    }

    if (this.mixer) {
      this.mixer.update(tickDeltaMs / 1000);
      return;
    }

    if (!this.container) return;
    if (!this.inputs.fallbackSpin) return;

    const degPerSec = Number(this.inputs.fallbackSpinDegPerSec ?? 0);
    const rad = THREE.MathUtils.degToRad(degPerSec) * (tickDeltaMs / 1000);
    this.container.rotation.y += rad;
  }

  onDestroy() {
    this.urlVersion++;
    this.mixer = null;
    this.container = null;
    this.modelRoot = null;
    if (this.outputs) {
      (this.outputs as any).objectRoot = null;
      (this.outputs as any).collider = null;
    }
  }
}

showcase.addEventListener('load', async function() {
  if (modelViewContainerObserver) {
    modelViewContainerObserver.disconnect();
    modelViewContainerObserver = null;
  }
  clearTagTitleActionGuardSubscription();
  currentSdkForTagTitleControl = null;

  let sdk: MpSdk.CommonMpSdk;
  try {
    sdk = await showcase.contentWindow.MP_SDK.connect(showcase, key, '3.6') as MpSdk.CommonMpSdk;
  }
  catch(e) {
    console.error(e);
    return;
  }

  sdkAnalyticsSubscriptions.forEach((sub) => {
    try {
      sub.cancel();
    } catch {}
  });
  sdkAnalyticsSubscriptions = [];
  floorTrackingInitialized = false;
  lastTrackedFloorId = null;
  activeSweepContext = null;

  logMattertagTitleAndIdList(sdk).catch((err: unknown) => {
    console.warn('[Mattertag] Failed to log title/id list', err);
  });

  // Override Tag disc icon to match the requested design (white disc + purple dot).
  setupCustomTagIcons(sdk).catch((err: unknown) => {
    console.warn('[TagIcon] Failed to setup custom tag icons', err);
  });

  setupModelViewContainerHidden();
  currentSdkForTagTitleControl = sdk;
  applyTagTitleBillboardVisibility(sdk, isTagTitleBillboardVisible());

  // Show try-on list icon after Showcase starts playing
  sdk.App.state.waitUntil((state) => state.phase === sdk.App.Phase.PLAYING)
    .then(() => setFloatingListVisible(true))
    .catch((err: unknown) => console.warn('Failed to wait for PLAYING phase', err));

  setupShareClickTracking();

  let vrTrackingInitialized = false;
  let wasInVr = false;
  const appStateSub = sdk.App.state.subscribe((appState: MpSdk.App.State) => {
    const inVr = appState.application === sdk.App.Application.WEBVR;
    if (!vrTrackingInitialized) {
      vrTrackingInitialized = true;
      wasInVr = inVr;
      return;
    }
    if (wasInVr === inVr) return;
    wasInVr = inVr;
    trackClick('vr', inVr ? 'enter' : 'exit');
  });
  sdkAnalyticsSubscriptions.push(appStateSub);

  const floorSub = sdk.Floor.current.subscribe((currentFloor: MpSdk.Floor.ObservableFloorData) => {
    const floorId = String(currentFloor.id ?? '').trim();
    const floorSequenceRaw = currentFloor.sequence;
    const floorName = String(currentFloor.name ?? '').trim();
    const floorSequence = Number(floorSequenceRaw);

    // Ignore "all floors" and transition states
    if (!floorId) return;
    if (!Number.isFinite(floorSequence) || floorSequence < 0) return;

    latestFloorContext = {
      floorId,
      floorSequence,
      floorName,
    };

    if (!floorTrackingInitialized) {
      floorTrackingInitialized = true;
      lastTrackedFloorId = floorId;
      return;
    }
    if (lastTrackedFloorId === floorId) return;

    trackClick('floor_switching', floorName || floorId, {
      floor_id: floorId,
      floor_sequence: floorSequence,
      floor_name: floorName,
    });
    lastTrackedFloorId = floorId;
  });
  sdkAnalyticsSubscriptions.push(floorSub);

  const sweepSub = sdk.Sweep.current.subscribe((currentSweep: MpSdk.Sweep.ObservableSweepData) => {
    const sweepId = getSweepId(currentSweep);
    if (!sweepId) {
      flushActiveSweepEngagement('switch');
      activeSweepContext = null;
      return;
    }

    if (activeSweepContext?.spotId === sweepId) return;

    flushActiveSweepEngagement('switch');

    const floorIdFromSweep = getSweepFloorId(currentSweep) ?? latestFloorContext.floorId;
    const floorSequenceFromSweep = getSweepFloorSequence(currentSweep);
    const floorSequence =
      floorSequenceFromSweep !== undefined ? floorSequenceFromSweep : latestFloorContext.floorSequence;

    if (floorIdFromSweep) latestFloorContext.floorId = floorIdFromSweep;
    if (floorSequence !== undefined) latestFloorContext.floorSequence = floorSequence;

    activeSweepContext = {
      spotId: sweepId,
      spotLabel: sweepLabelCache.get(sweepId) ?? sweepId,
      floorId: floorIdFromSweep,
      floorSequence,
      enteredAt: getNowMs(),
    };

    void getSweepLabelCached(sdk, sweepId).then((label) => {
      if (activeSweepContext?.spotId === sweepId) {
        activeSweepContext.spotLabel = label;
      }
    });
  });
  sdkAnalyticsSubscriptions.push(sweepSub);

  // Log the Mattertag id whenever a tag is clicked/touched
  sdk.on(sdk.Mattertag.Event.CLICK, async (tagSid: string) => {
    console.log('Mattertag clicked:', tagSid);

    // --- Tag tracking (GTM dataLayer) ---
    activeTagSid = tagSid;
    trackClick('tag', tagSid, { tag_sid: tagSid });

    const normalizedTagSid = String(tagSid ?? '').trim();
    const matchedConfigSid =
      Object.prototype.hasOwnProperty.call(MATTERTAG_PRODUCT_CONFIG, normalizedTagSid)
        ? normalizedTagSid
        : Object.keys(MATTERTAG_PRODUCT_CONFIG).find(
            (sid) => sid.toLowerCase() === normalizedTagSid.toLowerCase()
          ) ?? '';
    const cfg = matchedConfigSid ? MATTERTAG_PRODUCT_CONFIG[matchedConfigSid] : undefined;

    // Safety fallback: this tag should always open the product iframe even if config lookup fails.
    const fallbackIframeUrl =
      normalizedTagSid.toLowerCase() === 'zf6t44evjim'
        ? 'https://stg150.daytona-park.com/item/1111248902008'
        : '';

    const fixedItemCodes = cfg?.fixedItemCodes ?? [];
    const autoRules = cfg?.autoRules ?? [];
    const directIframeUrl = String(cfg?.iframeUrl ?? fallbackIframeUrl).trim();
    console.log('[TagRoute]', {
      clicked: tagSid,
      matchedConfigSid,
      hasConfig: Boolean(cfg),
      fixedItemCount: fixedItemCodes.length,
      autoRuleCount: autoRules.length,
      directIframeUrl,
    });
    if (!cfg && !directIframeUrl) {
      // 対象外のタグは従来通り何もしない（ログだけ残す）
      return;
    }
    if (cfg && fixedItemCodes.length === 0 && autoRules.length === 0 && !directIframeUrl) {
      // 対象外のタグは従来通り何もしない（ログだけ残す）
      return;
    }

    if (!isTagTitleBillboardVisible()) {
      // Mobile can still momentarily open the default tag UI. Close/disable it defensively.
      applyTagTitleAction(sdk, tagSid, false);
    }

    if (!modalBackdrop || !modalBody) return;
    if (directIframeUrl) {
      openIframeModal(directIframeUrl);
      return;
    }

    // クリックしたら即モーダルを出して「取得中」を表示
    modalBody.innerHTML = '<div style="padding:16px; color:#666; font-size:14px;">商品情報を取得中...</div>';
    setModalState('tag', { tagSid: tagSid });
    modalBackdrop.classList.add('visible');

    try {
      const products: ProductItem[] = [];

      // (固定) item_code をそのまま表示
      if (fixedItemCodes.length > 0) {
        const fixedProducts = await fetchProductsByItemCodes(fixedItemCodes);
        products.push(...fixedProducts);
      }

      // (自動) 条件検索で “最新N件” を追加（デフォルト10件、ルールごとに上書き可）
      if (autoRules.length > 0) {
        const cfgDefault = Number((cfg as any)?.autoLimit ?? DEFAULT_AUTO_LIMIT);
        const defaultLimit = Number.isFinite(cfgDefault) ? Math.max(1, Math.min(20, cfgDefault)) : DEFAULT_AUTO_LIMIT;

        const autoLists = await Promise.all(
          autoRules.map(async (ruleSpec: any) => {
            // 後方互換:
            // - 旧: { gender: 'WOMENS', ... }
            // - 新: { filters: {...}, limit: 5 }
            // - おまけ対応: { gender: 'WOMENS', ..., limit: 5 }

            let filters: any = ruleSpec;
            let ruleLimit: any = undefined;

            if (ruleSpec && typeof ruleSpec === 'object' && 'filters' in ruleSpec) {
              filters = ruleSpec.filters;
              ruleLimit = ruleSpec.limit;
            } else if (ruleSpec && typeof ruleSpec === 'object' && 'limit' in ruleSpec) {
              // limit をフィルタに混ぜない
              ruleLimit = (ruleSpec as any).limit;
              filters = { ...(ruleSpec as any) };
              delete (filters as any).limit;
            }

            const n = Number(ruleLimit ?? defaultLimit);
            const limit = Number.isFinite(n) ? Math.max(1, Math.min(20, n)) : defaultLimit;

            return await fetchLatestProductsByFilters(filters, limit);
          })
        );

        autoLists
          .flat()
          .filter((v): v is ProductItem => Boolean(v))
          .forEach((p) => products.push(p));
      }

      // 重複排除（id=item_code）
      const uniq = new Map<string, ProductItem>();
      products.forEach((p) => uniq.set(p.id, p));
      const merged = Array.from(uniq.values());

      if (merged.length === 0) {
        modalBody.innerHTML =
          '<div style="padding:16px; color:#b00020; font-size:14px;">商品情報の取得に失敗しました。</div>';
        return;
      }

      // 取得できた商品をキャッシュに格納
      merged.forEach((p) => productCache.set(p.id, p));

      // クリックしたタグに紐づく複数商品を表示（スライダー対応）
      sliderProducts = merged;

      // 既存のレイアウト/UIをそのまま使って表示
      showProductModal();
    } catch (e) {
      console.error(e);
      modalBody.innerHTML =
        '<div style="padding:16px; color:#b00020; font-size:14px;">商品情報の取得に失敗しました。</div>';
    }
  });

  // Setup a 3D clickable TAP sign texture in the scene (Bundle SDK Scene API)
  const setupTapSign = async () => {
    if (!TAP_SIGN_ENABLED) return;
    await sdk.App.state.waitUntil((state) => state.phase === sdk.App.Phase.PLAYING);

    const sdkAny = sdk as any;
    if (!sdkAny.Scene?.register || !sdkAny.Scene?.createObjects) {
      console.warn('[TapSign] Scene API is not available (Bundle SDK only). Skipping TAP sign.');
      return;
    }

    const disposable: Disposable | null = await sdkAny.Scene.register('daitona.tapSign', () => new TapSignComponent());
    if (!disposable) {
      console.warn('[TapSign] Failed to register component (register returned null).');
      return;
    }

    // Place at "entrance": pick a point on the floor in front of the initial camera.
    // We raycast from the lower center of the viewport to increase the chance of hitting the floor.
    let pos: MpSdk.Vector3 = { ...TAP_SIGN_POSITION };
    if (TAP_SIGN_PLACE_AT_ENTRANCE) {
      try {
        const w = showcase.clientWidth || Math.round(showcase.getBoundingClientRect().width);
        const h = showcase.clientHeight || Math.round(showcase.getBoundingClientRect().height);
        if (w > 0 && h > 0) {
          const p = await sdk.Renderer.getWorldPositionData(
            { x: Math.round(w * 0.5), y: Math.round(h * 0.85) },
            undefined,
            true
          );
          if (p.position) {
            pos = { x: p.position.x, y: p.position.y + TAP_SIGN_POSITION.y, z: p.position.z };
          }
        }
      } catch (e) {
        console.warn('[TapSign] Failed to compute entrance position, falling back to config position', e);
      }
    }
    console.log('[TapSign] position:', pos);

    const [obj] = await sdkAny.Scene.createObjects(1);
    const node = obj.addNode();
    const component = node.addComponent('daitona.tapSign', {
      url: TAP_SIGN_TEXTURE_PATH_IN_SHOWCASE,
      visible: true,
      position: { ...pos },
      orientation: { ...TAP_SIGN_ORIENTATION },
      diameter: TAP_SIGN_DIAMETER_METERS,
      billboard: TAP_SIGN_BILLBOARD,
    });
    node.start();

    (window as any).__TAP_SIGN_DISPOSABLE__ = disposable;
    (window as any).__TAP_SIGN_NODE__ = node;
    (window as any).__TAP_SIGN_COMPONENT__ = component;
  };

  setupTapSign().catch((err: unknown) => {
    console.error('[TapSign] Failed to setup TAP sign', err);
  });

  // Setup a 3D model (GLB/GLTF/FBX) in the scene (Bundle SDK Scene API)
  const setupAnimatedGlb = async () => {
    if (!CG_GLB_ENABLED) return;
    await sdk.App.state.waitUntil((state) => state.phase === sdk.App.Phase.PLAYING);

    const sdkAny = sdk as any;
    if (!sdkAny.Scene?.register || !sdkAny.Scene?.createObjects) {
      console.warn('[CG] Scene API is not available (Bundle SDK only). Skipping GLB.');
      return;
    }

    // Ensure the required loader is present in the showcase iframe
    const initialUrl = String(CG_GLB_PATH_IN_SHOWCASE ?? '');
    const cleanUrl = initialUrl.split('#')[0].split('?')[0];
    const ext = String(cleanUrl.split('.').pop() ?? '').toLowerCase();
    const loaderKey = ext === 'fbx' ? 'FBXLoader' : 'GLTFLoader';
    try {
      await waitFor(() => Boolean((showcase.contentWindow as any)?.THREE?.[loaderKey]), 8000, 50);
    } catch {
      console.error(`[CG] ${loaderKey} is not available in showcase window. Check bundle/showcase.html.`);
      return;
    }

    // Register a custom component
    const disposable: Disposable | null = await sdkAny.Scene.register('daitona.animatedGlb', () => new AnimatedGlbComponent());
    if (!disposable) {
      console.warn('[CG] Failed to register component (register returned null).');
      return;
    }

    let entrancePosition: MpSdk.Vector3 | null = null;
    if (CG_GLB_PLACE_AT_ENTRANCE) {
      // Place at "entrance": pick a point on the floor in front of the initial camera.
      // We raycast from the lower center of the viewport to increase the chance of hitting the floor.
      try {
        const w = showcase.clientWidth || Math.round(showcase.getBoundingClientRect().width);
        const h = showcase.clientHeight || Math.round(showcase.getBoundingClientRect().height);
        if (w > 0 && h > 0) {
          const p = await sdk.Renderer.getWorldPositionData(
            { x: Math.round(w * 0.5), y: Math.round(h * 0.85) },
            undefined,
            true
          );
          if (p.position) entrancePosition = p.position;
        }
      } catch (e) {
        console.warn('[CG] Failed to compute entrance position, falling back to config position', e);
      }
    }
    const modelPosition = CG_GLB_PLACE_AT_ENTRANCE ? (entrancePosition ?? CG_GLB_POSITION) : CG_GLB_POSITION;
    console.log('[CG] position:', modelPosition);

    // Create first model instance
    const [obj] = await sdkAny.Scene.createObjects(1);
    const node = obj.addNode();
    const component = node.addComponent('daitona.animatedGlb', {
      url: CG_GLB_PATH_IN_SHOWCASE,
      visible: CG_GLB_INITIAL_VISIBLE,
      position: { ...modelPosition },
      orientation: { ...CG_GLB_ORIENTATION },
      scale: { ...CG_GLB_SCALE },
      autoFitHeight: CG_GLB_AUTO_FIT_HEIGHT,
      fitHeightMeters: CG_GLB_FIT_HEIGHT_METERS,
      addLights: CG_GLB_ADD_LIGHTS,
      ambientLightIntensity: CG_GLB_AMBIENT_LIGHT_INTENSITY,
      directionalLightIntensity: CG_GLB_DIRECTIONAL_LIGHT_INTENSITY,
      forceUnlitMaterials: CG_GLB_FORCE_UNLIT_MATERIALS,
      fallbackSpin: CG_GLB_FALLBACK_SPIN,
      fallbackSpinDegPerSec: CG_GLB_FALLBACK_SPIN_DEG_PER_SEC,
    });
    node.start();

    // Optional: keep a reference in case you want to remove later
    (window as any).__CG_GLB_DISPOSABLE__ = disposable;
    (window as any).__CG_GLB_NODE__ = node;
    (window as any).__CG_GLB_COMPONENT__ = component;

    // Create second model instance
    if (CG_GLB_SECOND_ENABLED) {
      const secondPosition: MpSdk.Vector3 = { ...CG_GLB_SECOND_POSITION };
      console.log('[CG] position(2):', secondPosition);

      const [obj2] = await sdkAny.Scene.createObjects(1);
      const node2 = obj2.addNode();
      const component2 = node2.addComponent('daitona.animatedGlb', {
        url: CG_GLB_SECOND_PATH_IN_SHOWCASE,
        visible: CG_GLB_INITIAL_VISIBLE,
        position: secondPosition,
        orientation: { ...CG_GLB_ORIENTATION },
        scale: { ...CG_GLB_SECOND_SCALE },
        autoFitHeight: CG_GLB_AUTO_FIT_HEIGHT,
        fitHeightMeters: CG_GLB_FIT_HEIGHT_METERS,
        addLights: CG_GLB_ADD_LIGHTS,
        ambientLightIntensity: CG_GLB_AMBIENT_LIGHT_INTENSITY,
        directionalLightIntensity: CG_GLB_DIRECTIONAL_LIGHT_INTENSITY,
        forceUnlitMaterials: CG_GLB_FORCE_UNLIT_MATERIALS,
        fallbackSpin: CG_GLB_FALLBACK_SPIN,
        fallbackSpinDegPerSec: CG_GLB_FALLBACK_SPIN_DEG_PER_SEC,
      });
      node2.start();

      (window as any).__CG_GLB_NODE_2__ = node2;
      (window as any).__CG_GLB_COMPONENT_2__ = component2;
    }

    // Toggle UI
    if (CG_GLB_ENABLE_TOGGLE_BUTTON) {
      const btn = ensureCgToggleButton();
      if (btn) btn.style.display = 'block';
      updateCgToggleButton();
    }
  };

  setupAnimatedGlb().catch((err: unknown) => {
    console.error('[CG] Failed to setup animated GLB', err);
  });

  console.log('%c  Hello Bundle SDK! ', 'background: #333333; color: #00dd00');
  console.log(sdk);
});

if (modalClose && modalBackdrop) {
  modalClose.addEventListener('click', () => {
    if (currentModalType === 'tag') {
      trackClick('tag_closebutton', 'x', { tag_sid: activeTagSid });
    }
    if (currentModalType === 'tryon') {
      trackClick('tryonlist_close', 'x');
    }
    if (currentModalType === 'ec_iframe') {
      trackEcIframeClose('x');
    }
    if (currentModalType === 'plusmirror') {
      trackPlusMirrorClose('x');
    }

    destroyDigitalMirrorClient();
    clearModalState();
    hideModal();
  });
}
